import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "./axiosBaseQuery";
import DialerTreeConfig from "../app/config";
import { IncomingCallEvent } from "@/model/interface/IncomingCallEvent";
import User from "../model/interface/User";
import LoginResponse from "../model/interface/LoginResponse";
import LoginData from "../model/interface/LoginData";
import {
  TotpSetup,
  TwoFactorChallenge,
  TwoFactorStatus,
  TwoFactorVerifyData,
  UserDevice,
} from "@/model/interface/TwoFactor";
import RegisterData from "../model/interface/RegisterData";
import RegisterResponse from "../model/interface/RegisterResponse";
import { AuthIdentities, OAuthLoginStart } from "@/model/interface/OAuthLogin";
import { MembershipInvitationDetails } from "@/model/interface/MembershipInvitationDetails";
import {
  InviteValidation,
  RegistrationRequest,
  RegistrationRequestData,
} from "@/model/interface/RegistrationRequest";
import FileResponse, { AudioClip, GenerateAudioClipRequest } from "@/model/interface/FileResponse";
import { AudioLibrary } from "@/enum/AudioLibrary";
import { LiveMonitorEntry, SupervisorSession } from "@/model/interface/Monitor";
import FileData from "../model/interface/FileData";
import { AxiosRequestHeaders } from "axios";
import ApiUser from "../model/interface/api/ApiUser";
import Integration, { SalesforceRealtimeStatus, SalesforceUser } from "@/model/interface/Integration";
import { BillingInterval, SubscriptionPlan, SubscriptionPreview } from "@/model/interface/SubscriptionPlan";
import { AddOn, OrgAddOnPriceEntry } from "@/model/interface/AddOn";
import { RecordingAction } from "@/enum/RecordingAction";
import IntegrationFormData from "@/model/interface/IntegrationFormData";
import ConnectIntegration from "@/model/interface/ConnectIntegrationResponse";
import SalesforceAuthZ from "@/model/interface/SalesforceAuthZ";
import Call, {
  AddCallParticipantRequest,
  CallParticipant,
  CallPlatformRequest,
  CallScript,
  CallScriptRequest,
  CallStats,
  CallTransfer,
  CallTransferRequest,
} from "@/model/interface/Call";
import Team, { TeamRequest } from "@/model/interface/Team";
import {
  findCachedDealStage,
  findCachedDisposition,
  markNotificationsSeenInAllCaches,
  patchCallInAllCaches,
  patchContactActiveDealInAllCaches,
  patchDealInAllCaches,
  reorderDealStagesInAllCaches,
  undoOnError,
} from "@/util/optimisticCache";
import { DEVICE_PLATFORM } from "@/util/device";
import Organisation, { BillingDetails } from "@/model/interface/Organisation";
import { Invoice, InvoicePdfLink } from "@/model/interface/Invoice";
import { ReferralOverview, ReferralProgramStatus } from "@/model/interface/Referral";
import OrganisationFormData from "@/model/interface/OrganisationFormData";
import PhoneNumber, { DialerNumbersOverview, PurchasePhoneNumberRequest } from "@/model/interface/PhoneNumber";
import { settingState } from "@/model/interface/SettingState";
import AvailablePhoneNumber from "@/model/interface/AvailablePhoneNumber";
import PhoneNumberSearchParams from "@/model/interface/PhoneNumberSearchParams";
import { StripeSubscriptionData } from "@/model/interface/StripeSubscriptionData";
import { IdentityVerificationData } from "@/model/interface/IdentityVerificationData";
import { Deposit } from "@/model/interface/Deposit";
import { Charge } from "@/model/interface/Charge";
import { ChargeType } from "@/enum/ChargeType";
import { BillingOverview, BillingUsage } from "@/model/interface/BillingOverview";
import { PaymentIntentData } from "@/model/interface/PaymentIntentData";
import { PaymentMethodData } from "@/model/interface/PaymentMethodData";
import { CallFlow, CallFlowRevision, CallFlowRunStep } from "@/model/interface/CallFlow";
import { UpdateCallFlowRequest } from "@/model/interface/api/UpdateCallFlowRequest";
import { CreateCallFlowRequest } from "@/model/interface/api/CreateCallFlowRequest";
import Membership, {
  ContactWarningPreferenceRequest,
  DialerSeatRequest,
  DisplayNameRequest,
  ExportAccessRequest,
  UserPresence,
  UserPresenceStatusRequest,
  VoicemailGreetingRequest,
  WorkingHoursRequest,
} from "@/model/interface/Membership";
import type { ExportDownload, ExportRequest, ExportSummary } from "@/model/interface/DataExport";
import { UpdateMemberRoleRequest } from "@/model/interface/api/UpdateMemberRoleRequest";
import { InviteMemberRequest } from "@/model/interface/api/InviteMemberRequest";
import { InitiateCallRequest } from "@/model/interface/api/InitiateCallRequest";
import { BusyTeammateRinging, TelephonySettings } from "@/model/interface/TelephonySettings";
import CampaignContact, {
  CallbackRequest,
  DialerQueueCounts,
  RequeueRequest,
  VoicemailDropStatus,
} from "@/model/interface/CampaignContact";
import type {
  OptOutKeywordSetting,
  QuietHoursPolicySetting,
  SmsOptOutKeyword,
} from "@/model/interface/OrganisationSettings";
import { DialerQueueGroup } from "@/enum/DialerQueueGroup";
import DialerSession, {
  AbandonmentReport,
  DialerReport,
  DialerReportFilter,
  DialerReturnCallInfo,
  DialerSessionEndReason,
  DialerSessionHistoryRow,
  DialerSessionSnapshot,
} from "@/model/interface/DialerSession";
import Campaign, {
  AiCampaignLiveResponse,
  CampaignAudiencePreview,
  CampaignAudienceRequest,
  CampaignFormData,
} from "@/model/interface/Campaign";
import {
  CampaignCompliancePreflight,
  QuietHoursCheckResult,
  SmsEligibility,
  A2pPricingRates,
  DncLookupPrices,
  DncLookupResponse,
} from "@/model/interface/Compliance";
import Contact, { LeadSummary } from "@/model/interface/Contact";
import { ContactType } from "@/enum/ContactType";
import PagedResponse from "@/model/interface/PagedResponse";
import CrmSyncLog, { CrmSyncLogSummary } from "@/model/interface/CrmSyncLog";
import OrgActivityLog from "@/model/interface/OrgActivityLog";
import CrmPushJob, { CrmPushJobSummary } from "@/model/interface/CrmPushJob";
import ContactList from "@/model/interface/ContactList";
import ContactListContact from "@/model/interface/ContactListContact";
import ContactActivitySummary from "@/model/interface/ContactActivitySummary";
import {
  ContactImportSummary,
  ImportEntityType,
  ImportMappingSpec,
  ImportPreviewRow,
  ImportRowError,
  ImportRunOptions,
  ImportRunSummary,
  ImportSetup,
  ImportValueField,
} from "@/model/interface/DataImport";
import Conversation from "@/model/interface/Conversation";
import Message from "@/model/interface/Message";
import {
  AttentionCount,
  CommunicationItemFilter,
  CommunicationItemPage,
  CommunicationItemPageParam,
} from "@/model/interface/CommunicationItem";
import type { TextsReport } from "@/model/interface/Message";
import MessageNote from "@/model/interface/MessageNote";
import DncEntry, { DncType, EmailSuppression, UnsubscribeTarget } from "@/model/interface/DncEntry";
import ConsentEvent from "@/model/interface/ConsentEvent";
import CallDisposition from "@/model/interface/CallDisposition";
import { Site, Page, SiteForm, PageTemplate, FormSubmission } from "@/model/interface/Site";
import { DashboardSummary } from "@/model/interface/DashboardSummary";
import Deal, { PipelineSummary } from "@/model/interface/Deal";
import DealStage, { DealStageSettings, DealStageUsage } from "@/model/interface/DealStage";
import { DealStageCategory } from "@/enum/DealStageCategory";
import DealActivity from "@/model/interface/DealActivity";
import Property from "@/model/interface/Property";
import ExternalPropertyResult from "@/model/interface/ExternalPropertyResult";
import {
  PropertyDataUsage,
  PropertyPreviewResult,
  DuplicateSearchWarning,
  PropertyBuildStatus,
} from "@/model/interface/PropertyListings";
import PropertySearchCriteria from "@/model/interface/PropertySearchCriteria";
import Tag from "@/model/interface/Tag";
import Task, { TaskStatus, TaskPriority } from "@/model/interface/Task";
import type { Workflow, WorkflowExecution, WorkflowStatus, WorkflowTriggerType, WorkflowActionType, WorkflowWebhookCredential } from "@/model/interface/Workflow";
import A2pRegistration from "@/model/interface/A2pRegistration";
import type { A2pCampaign } from "@/model/interface/A2pRegistration";
import type Notification from "@/model/interface/Notification";
import type { NotificationPreference } from "@/model/interface/NotificationPreference";
import type {
  AiAgent,
  AiAgentCall,
  AiAgentListing,
  AiListingCharge,
  LiveAiCall,
  TranscriptLine,
  VoiceOption,
} from "@/model/interface/AiAgent";
import type { FeatureCatalogEntry, OrgFeature, OrgFeatureToggleRequest } from "@/model/interface/Feature";
import type { SkipTraceJob, SkipTracePreview, SkipTraceResult } from "@/model/interface/SkipTrace";
import type { SkipTraceScope } from "@/enum/SkipTraceScope";
import type { SkipTraceMode } from "@/enum/SkipTraceMode";
import type { DncCheckMode } from "@/enum/DncCheckMode";
import type { Checklist, OnboardingState, OnboardingSubmission } from "@/model/interface/Onboarding";
import type { SupportChatToken } from "@/model/interface/Support";
import type { SequenceEnrollment, SequenceStats } from "@/model/interface/Sequence";

export const restApiTagTypes = [
  "Teams",
  "CallParticipants",
  "MemberAvailability",
  "AiAgentLiveCalls",
  "Home",
  "Integrations",
  "PushJobs",
  "User",
  "File",
  "AudioClips",
  "Email",
  "Organisations",
  "PhoneNumbers",
  "AvailablePhoneNumbers",
  "Deposits",
  "Charges",
  "Billing",
  "Subscription",
  "PaymentMethods",
  "PaymentMethod",
  "CallFlows",
  "CallFlowRevisions",
  "Memberships",
  "Calls",
  "Contacts",
  "Campaigns",
  "CampaignContacts",
  "DialerSessions",
  "DialerQueue",
  "DialerPreview",
  "DialerNumbers",
  "CampaignCompliance",
  "ContactList",
  "ContactListContacts",
  "SkipTraceJobs",
  "Conversations",
  "Messages",
  "CommunicationItems",
  "DncEntries",
  "EmailSuppressions",
  "ConsentEvents",
  "CallDispositions",
  "CallScripts",
  "Deals",
  "DealStages",
  "DealActivities",
  "LeadSummary",
  "Properties",
  "PropertyDataUsage",
  "PropertyDataSearches",
  "Tags",
  "Tasks",
  "Workflows",
  "WorkflowExecutions",
  "WebhookCredentials",
  "A2pRegistration",
  "A2pCampaigns",
  "SmsEligibility",
  "QuietHoursPolicy",
  "OptOutKeywords",
  "Notifications",
  "NotificationPreferences",
  "AiAgents",
  "AiAgentCalls",
  "AiMarketplace",
  "SyncLogs",
  "PodioApps",
  "Features",
  "Onboarding",
  "Checklist",
  "Sites",
  "SiteTemplates",
  "TwoFactor",
  "Devices",
  "AuthIdentities",
  "Imports",
  "Exports",
  "Invoices",
  "BillingDetails",
  "Referrals",
] as const;

export const restApi = createApi({
  reducerPath: "restApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${DialerTreeConfig.baseAPIEndpoint}`,
  }),
  tagTypes: restApiTagTypes,
  endpoints: (builder) => ({
    getSiteTemplates: builder.query<PageTemplate[], void>({
      query: () => ({ url: "/v1/sites/templates", method: "get" }),
      providesTags: ["SiteTemplates"],
    }),
    getSites: builder.query<Site[], void>({
      query: () => ({ url: "/v1/sites", method: "get" }),
      providesTags: ["Sites"],
    }),
    getSite: builder.query<Site, string>({
      query: (id) => ({ url: `/v1/sites/${id}`, method: "get" }),
      providesTags: ["Sites"],
    }),
    createSite: builder.mutation<Site, { sourceTemplateKey: string; name: string; slug?: string }>({
      query: (data) => ({ url: "/v1/sites", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    updateSite: builder.mutation<Site, { id: string; data: Partial<Site> }>({
      query: ({ id, data }) => ({ url: `/v1/sites/${id}`, method: "patch", data }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    deleteSite: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/sites/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    publishSite: builder.mutation<Site, string>({
      query: (id) => ({ url: `/v1/sites/${id}/publish`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    unpublishSite: builder.mutation<Site, string>({
      query: (id) => ({ url: `/v1/sites/${id}/unpublish`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    generateSitePreviewToken: builder.mutation<Site, string>({
      query: (id) => ({ url: `/v1/sites/${id}/preview-token`, method: "post" }),
    }),
    getSitePages: builder.query<Page[], string>({
      query: (id) => ({ url: `/v1/sites/${id}/pages`, method: "get" }),
      providesTags: ["Sites"],
    }),
    updateSitePage: builder.mutation<Page, { siteId: string; pageId: string; data: Partial<Page> }>({
      query: ({ siteId, pageId, data }) => ({
        url: `/v1/sites/${siteId}/pages/${pageId}`,
        method: "patch",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    getSiteForms: builder.query<SiteForm[], string>({
      query: (id) => ({ url: `/v1/sites/${id}/forms`, method: "get" }),
      providesTags: ["Sites"],
    }),
    getSiteLeads: builder.query<PagedResponse<FormSubmission>, { siteId: string; page: number; size: number }>({
      query: ({ siteId, page, size }) => ({ url: `/v1/sites/${siteId}/leads`, method: "get", params: { page, size } }),
      providesTags: ["Sites"],
    }),
    getSiteRecentLeadCount: builder.query<number, string>({
      query: (id) => ({ url: `/v1/sites/${id}/leads/recent-count`, method: "get" }),
      providesTags: ["Sites"],
    }),
    markSiteLeadAsSpam: builder.mutation<FormSubmission, { siteId: string; submissionId: string }>({
      query: ({ siteId, submissionId }) => ({ url: `/v1/sites/${siteId}/leads/${submissionId}/spam`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites", "Contacts", "Conversations", "CommunicationItems", "Deals", "Tasks"]),
    }),
    getDashboardSummary: builder.query<DashboardSummary, { from?: string; to?: string } | void>({
      query: (params) => ({ url: "/v1/dashboard/summary", method: "get", params: params ?? undefined }),
      providesTags: ["Home"],
    }),
    updateSiteForm: builder.mutation<SiteForm, { siteId: string; formId: string; data: Partial<SiteForm> }>({
      query: ({ siteId, formId, data }) => ({
        url: `/v1/sites/${siteId}/forms/${formId}`,
        method: "patch",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Sites"]),
    }),
    getHome: builder.query<ApiUser, void>({
      query: () => ({
        url: `/v1/users/me`,
        method: "get",
      }),
      providesTags: ["Home"],
    }),
    getUser: builder.query<User, string>({
      query: (id) => ({
        url: `/v1/users/${id}`,
        method: "get",
      }),
      providesTags: ["User"],
    }),
    putHome: builder.mutation<ApiUser, User>({
      query: (userData) => ({
        url: `/v1/users/${userData.id}`,
        method: "put",
        data: userData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    setActiveOrganisation: builder.mutation<void, Organisation>({
      query: (organisation) => ({
        url: `/v1/users/set-organisation/${organisation.id}`,
        method: "put",
      }),
      async onQueryStarted(_, lifecycle) {
        try {
          await lifecycle.queryFulfilled;
        } catch {
          return;
        }
        lifecycle.dispatch(restApi.util.resetApiState());
      },
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    getUserPresence: builder.query<UserPresence, void>({
      query: () => ({
        url: `/v1/users/presence`,
        method: "get",
      }),
      providesTags: ["User"],
    }),
    updateUserPresence: builder.mutation<UserPresence, UserPresenceStatusRequest>({
      query: (body) => ({
        url: `/v1/users/presence`,
        method: "put",
        data: body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["User", "MemberAvailability"]),
    }),
    updateWorkingHours: builder.mutation<UserPresence, WorkingHoursRequest>({
      query: (body) => ({
        url: `/v1/users/presence/working-hours`,
        method: "put",
        data: body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["User", "MemberAvailability"]),
    }),
    setMemberWorkingHours: builder.mutation<UserPresence, { membershipId: string; data: WorkingHoursRequest }>({
      query: ({ membershipId, data }) => ({
        url: `/v1/memberships/${membershipId}/working-hours`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["User", "MemberAvailability"]),
    }),
    postLogin: builder.mutation<LoginResponse | TwoFactorChallenge, LoginData>({
      query: (loginData) => ({
        url: `/v1/auth/login`,
        method: "post",
        data: loginData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    twoFactorEnroll: builder.mutation<TwoFactorChallenge, { challengeToken: string; phone: string }>({
      query: (body) => ({
        url: `/v1/auth/2fa/enroll`,
        method: "post",
        data: body,
      }),
    }),
    twoFactorSend: builder.mutation<TwoFactorChallenge, { challengeToken: string; method?: string }>({
      query: (body) => ({
        url: `/v1/auth/2fa/send`,
        method: "post",
        data: body,
      }),
    }),
    twoFactorVerify: builder.mutation<LoginResponse, TwoFactorVerifyData>({
      query: (body) => ({
        url: `/v1/auth/2fa/verify`,
        method: "post",
        data: body,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    getTwoFactorStatus: builder.query<TwoFactorStatus, void>({
      query: () => ({ url: `/v1/users/me/2fa`, method: "get" }),
      providesTags: ["TwoFactor"],
    }),
    twoFactorSmsSetup: builder.mutation<TwoFactorStatus, { phone: string }>({
      query: (body) => ({
        url: `/v1/users/me/2fa/sms/setup`,
        method: "post",
        data: body,
      }),
    }),
    twoFactorSmsConfirm: builder.mutation<void, { code: string }>({
      query: (body) => ({
        url: `/v1/users/me/2fa/sms/confirm`,
        method: "post",
        data: body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["TwoFactor", "Home"]),
    }),
    twoFactorTotpSetup: builder.mutation<TotpSetup, void>({
      query: () => ({ url: `/v1/users/me/2fa/totp/setup`, method: "post" }),
    }),
    twoFactorTotpConfirm: builder.mutation<void, { code: string }>({
      query: (body) => ({
        url: `/v1/users/me/2fa/totp/confirm`,
        method: "post",
        data: body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["TwoFactor", "Home"]),
    }),
    twoFactorSendCode: builder.mutation<void, void>({
      query: () => ({ url: `/v1/users/me/2fa/send`, method: "post" }),
    }),
    disableTwoFactor: builder.mutation<void, { code: string }>({
      query: (body) => ({
        url: `/v1/users/me/2fa`,
        method: "delete",
        data: body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["TwoFactor", "Home"]),
    }),
    getDevices: builder.query<UserDevice[], void>({
      query: () => ({ url: `/v1/users/me/devices`, method: "get" }),
      providesTags: ["Devices"],
    }),
    revokeDevice: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `/v1/users/me/devices/${deviceId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Devices"]),
    }),
    startOAuthLogin: builder.mutation<
      OAuthLoginStart,
      { provider: string; invite?: string; memberInvite?: string }
    >({
      query: ({ provider, invite, memberInvite }) => ({
        url: `/v1/auth/oauth/${provider}/start`,
        method: "get",
        params: {
          ...(invite ? { invite } : {}),
          ...(memberInvite ? { memberInvite } : {}),
        },
      }),
    }),
    completeOAuthLogin: builder.mutation<
      LoginResponse | TwoFactorChallenge,
      {
        provider: string;
        code: string;
        state: string;
        deviceToken?: string;
        deviceName?: string;
        devicePlatform?: string;
      }
    >({
      query: ({ provider, ...body }) => ({
        url: `/v1/auth/oauth/${provider}/callback`,
        method: "post",
        data: body,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    getAuthIdentities: builder.query<AuthIdentities, void>({
      query: () => ({ url: `/v1/auth/identities`, method: "get" }),
      providesTags: ["AuthIdentities"],
    }),
    unlinkAuthIdentity: builder.mutation<void, number>({
      query: (id) => ({
        url: `/v1/auth/identities/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AuthIdentities"]),
    }),
    postRegister: builder.mutation<RegisterResponse, RegisterData>({
      query: ({ invite, ...registerData }) => ({
        url: `/v1/auth/register`,
        method: "post",
        data: { ...registerData, devicePlatform: DEVICE_PLATFORM },
        params: invite ? { invite } : undefined,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    submitRegistrationRequest: builder.mutation<RegistrationRequest, RegistrationRequestData>({
      query: (requestData) => ({
        url: `/v1/registration-requests`,
        method: "post",
        data: requestData,
      }),
    }),
    getReferralProgramStatus: builder.query<ReferralProgramStatus, void>({
      query: () => ({
        url: `/v1/public/referrals`,
        method: "get",
      }),
    }),
    validateInvite: builder.query<InviteValidation, string>({
      query: (token) => ({
        url: `/v1/registration-requests/validate`,
        method: "get",
        params: { token },
      }),
    }),
    getMembershipInvitation: builder.query<MembershipInvitationDetails, string>({
      query: (token) => ({ url: `/v1/auth/invitation/${token}`, method: "get" }),
    }),
    postRegisterViaInvite: builder.mutation<RegisterResponse, RegisterData & { token: string }>({
      query: ({ token, invite, ...registerData }) => ({
        url: `/v1/auth/register-via-invite`,
        method: "post",
        data: { ...registerData, devicePlatform: DEVICE_PLATFORM },
        params: { token },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: `/v1/auth/forgot-password`,
        method: "post",
        data: body,
      }),
    }),
    resetPassword: builder.mutation<void, { token: string; newPassword: string }>({
      query: (body) => ({
        url: `/v1/auth/reset-password`,
        method: "post",
        data: body,
      }),
    }),
    postFile: builder.mutation<FileResponse, FileData>({
      query: ({ file, entity, entityId }) => {
        const formData = new FormData();
        formData.append("file[]", file);
        entity && formData.append("entity", entity);
        entityId && formData.append("entityId", entityId);

        return {
          url: `/v1/files`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data;",
          } as AxiosRequestHeaders,
          data: formData,
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ["File"]),
    }),
    getIntegrations: builder.query<Integration[], void>({
      query: () => ({
        url: `/v1/integrations`,
        method: "get",
      }),
      providesTags: ["Integrations"],
    }),
    createIntegration: builder.mutation<Integration, IntegrationFormData>({
      query: (integrationData) => ({
        url: `/v1/integrations`,
        method: "post",
        data: integrationData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Integrations"]),
    }),
    syncIntegration: builder.mutation<Integration, Integration>({
      query: (integration) => ({
        url: `/v1/integrations/${integration.id}/sync`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Integrations"]),
    }),
    deleteIntegration: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/integrations/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations", "SyncLogs"]),
    }),
    postAuthZSalesforce: builder.mutation<ConnectIntegration, Integration>({
      query: (integration) => ({
        url: `/v1/integrations/salesforce/login/${integration.id}`,
        method: "get",
      }),
    }),
    postAuthSalesforce: builder.mutation<Integration, SalesforceAuthZ>({
      query: (authz) => ({
        url: `/v1/integrations/salesforce/callback`,
        method: "post",
        data: authz,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Integrations"]),
    }),
    getSalesforceRealtimeStatus: builder.query<SalesforceRealtimeStatus, void>({
      query: () => ({
        url: `/v1/integrations/salesforce/realtime`,
        method: "get",
      }),
      providesTags: ["Integrations"],
    }),
    checkSalesforceRealtime: builder.mutation<SalesforceRealtimeStatus, void>({
      query: () => ({
        url: `/v1/integrations/salesforce/realtime/check`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Integrations"]),
    }),
    postAuthZHubSpot: builder.mutation<{ url: string }, Integration>({
      query: (integration) => ({
        url: `/v1/integrations/hubspot/login/${integration.id}`,
        method: "get",
      }),
    }),
    postAuthHubSpot: builder.mutation<string, { code: string; integrationId: string }>({
      query: (data) => ({
        url: `/v1/integrations/hubspot/callback`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    postAuthZGhl: builder.mutation<{ url: string }, Integration>({
      query: (integration) => ({
        url: `/v1/integrations/ghl/login/${integration.id}`,
        method: "get",
      }),
    }),
    postAuthGhl: builder.mutation<string, { code: string; integrationId: string }>({
      query: (data) => ({
        url: `/v1/integrations/ghl/callback`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    connectInvestorFuse: builder.mutation<
      string,
      { integrationId: string; apiKey: string; webhookKey?: string; teamId?: string }
    >({
      query: (data) => ({
        url: `/v1/integrations/investorfuse/connect`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    connectReiSift: builder.mutation<string, { integrationId: string; apiKey: string }>({
      query: (data) => ({
        url: `/v1/integrations/reisift/connect`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    getHubSpotOwners: builder.query<Array<{ id: string; email: string; firstName: string; lastName: string }>, void>({
      query: () => ({
        url: `/v1/integrations/hubspot/owners`,
        method: "get",
      }),
    }),
    setHubSpotUserMapping: builder.mutation<Membership, { membershipId: string; hubspotOwnerId: string | null }>({
      query: ({ membershipId, hubspotOwnerId }) => ({
        url: `/v1/integrations/hubspot/user-mapping/${membershipId}`,
        method: "put",
        data: { hubspotOwnerId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships"]),
    }),
    postAuthZPodio: builder.mutation<{ url: string }, Integration>({
      query: (integration) => ({
        url: `/v1/integrations/podio/login/${integration.id}`,
        method: "get",
      }),
    }),
    postAuthPodio: builder.mutation<string, { code: string; integrationId: string }>({
      query: (data) => ({
        url: `/v1/integrations/podio/callback`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    getPodioOrganizations: builder.query<Array<{ orgId: number; name: string }>, void>({
      query: () => ({
        url: `/v1/integrations/podio/organizations`,
        method: "get",
      }),
    }),
    getPodioWorkspaces: builder.query<Array<{ spaceId: number; name: string }>, number>({
      query: (orgId) => ({
        url: `/v1/integrations/podio/workspaces/${orgId}`,
        method: "get",
      }),
    }),
    getPodioApps: builder.query<Array<{ appId: number; name: string; itemName: string }>, number>({
      query: (workspaceId) => ({
        url: `/v1/integrations/podio/apps/${workspaceId}`,
        method: "get",
      }),
      providesTags: ["PodioApps"],
    }),
    getPodioAppFields: builder.query<Array<{ fieldId: number; externalId: string; label: string; type: string }>, number>({
      query: (appId) => ({
        url: `/v1/integrations/podio/fields/${appId}`,
        method: "get",
      }),
    }),
    createPodioCallsApp: builder.mutation<{ appId: number; fieldMapping: Record<string, string> }, { iconId: number }>({
      query: (data) => ({
        url: `/v1/integrations/podio/create-calls-app`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations", "PodioApps"]),
    }),
    createPodioContactsApp: builder.mutation<{ appId: number; fieldMapping: Record<string, string> }, { iconId: number }>({
      query: (data) => ({
        url: `/v1/integrations/podio/create-contacts-app`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations", "PodioApps"]),
    }),
    setupPodioWebhook: builder.mutation<{ webhookUrl: string; secret: string }, void>({
      query: () => ({
        url: `/v1/integrations/podio/webhook/setup`,
        method: "post",
      }),
    }),
    searchPodioIcons: builder.query<number[], string>({
      query: (search) => ({
        url: `/v1/integrations/podio/icons`,
        method: "get",
        params: { query: search },
      }),
    }),
    linkPodioApp: builder.mutation<
      { appId: number; fieldMapping: Record<string, string> },
      { type: "contacts" | "calls"; appId: number }
    >({
      query: (data) => ({
        url: `/v1/integrations/podio/link-app`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    setPodioUserMapping: builder.mutation<Membership, { membershipId: string; podioUserId: string | null }>({
      query: ({ membershipId, podioUserId }) => ({
        url: `/v1/integrations/podio/user-mapping/${membershipId}`,
        method: "put",
        data: { podioUserId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships"]),
    }),
    updateIntegrationSettings: builder.mutation<Integration, { id: string; settings: Record<string, any> }>({
      query: ({ id, settings }) => ({
        url: `/v1/integrations/${id}/settings`,
        method: "put",
        data: settings,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    updateIntegrationSyncSettings: builder.mutation<
      Integration,
      { id: string; syncEnabled: boolean; syncDirection?: string }
    >({
      query: ({ id, syncEnabled, syncDirection }) => ({
        url: `/v1/integrations/${id}/sync-settings`,
        method: "put",
        data: { syncEnabled, syncDirection },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    updateFieldMapping: builder.mutation<Integration, { id: string; fieldMapping: Record<string, any> }>({
      query: ({ id, fieldMapping }) => ({
        url: `/v1/integrations/${id}/field-mapping`,
        method: "put",
        data: fieldMapping,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Integrations"]),
    }),
    getDefaultFieldMappings: builder.query<Record<string, Record<string, string>>, string>({
      query: (id) => ({
        url: `/v1/integrations/${id}/field-mapping/defaults`,
        method: "get",
      }),
    }),
    getSalesforceUsers: builder.query<SalesforceUser[], void>({
      query: () => ({
        url: `/v1/integrations/salesforce/users`,
        method: "get",
      }),
    }),
    setSalesforceUserMapping: builder.mutation<Membership, { membershipId: string; salesforceUserId: string | null }>({
      query: ({ membershipId, salesforceUserId }) => ({
        url: `/v1/integrations/salesforce/user-mapping/${membershipId}`,
        method: "put",
        data: { salesforceUserId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships"]),
    }),
    getSyncLogs: builder.query<
      PagedResponse<CrmSyncLog>,
      { status?: string; page?: number; size?: number; integrationId?: string }
    >({
      query: ({ status, page = 0, size = 20, integrationId }) => ({
        url: `/v1/integrations/sync-logs`,
        method: "get",
        params: { status, page, size, integrationId },
      }),
      providesTags: ["SyncLogs"],
    }),
    getSyncLogSummary: builder.query<CrmSyncLogSummary, { integrationId?: string } | void>({
      query: (arg) => ({
        url: `/v1/integrations/sync-logs/summary`,
        method: "get",
        params: { integrationId: arg?.integrationId },
      }),
      providesTags: ["SyncLogs"],
    }),
    getActivityLog: builder.query<PagedResponse<OrgActivityLog>, { category?: string; page?: number; size?: number }>({
      query: ({ category, page = 0, size = 25 }) => ({
        url: `/v1/activity-log`,
        method: "get",
        params: { category, page, size },
      }),
    }),
    getPushJobs: builder.query<
      PagedResponse<CrmPushJob>,
      { service: string; status?: string; page?: number; size?: number }
    >({
      query: ({ service, status, page = 0, size = 25 }) => ({
        url: `/v1/integrations/push-jobs`,
        method: "get",
        params: { service, status, page, size },
      }),
      providesTags: ["PushJobs"],
    }),
    getPushJobSummary: builder.query<CrmPushJobSummary, { service: string }>({
      query: ({ service }) => ({
        url: `/v1/integrations/push-jobs/summary`,
        method: "get",
        params: { service },
      }),
      providesTags: ["PushJobs"],
    }),
    retryPushJob: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/integrations/push-jobs/${id}/retry`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PushJobs"]),
    }),
    getSalesforceListViews: builder.query<Array<{ id: string; label: string; developerName: string }>, string>({
      query: (objectType) => ({
        url: `/v1/integrations/salesforce/list-views`,
        method: "get",
        params: { objectType },
      }),
    }),
    importSalesforceListView: builder.mutation<
      { imported: number; newContacts: number; total: number },
      { objectType: string; listViewId: string; campaignId?: string; contactListId?: string }
    >({
      query: (data) => ({
        url: `/v1/integrations/salesforce/list-views/import`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "Campaigns", "ContactList"]),
    }),
    describeSalesforceObject: builder.query<Array<{ name: string; label: string; type: string }>, string>({
      query: (objectType) => ({
        url: `/v1/integrations/salesforce/describe/${objectType}`,
        method: "get",
      }),
    }),
    retrySyncLog: builder.mutation<void, string>({
      query: (logId) => ({
        url: `/v1/integrations/sync-logs/${logId}/retry`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["SyncLogs"]),
    }),
    createCall: builder.mutation<{ callSid: string }, Call>({
      query: (callData) => ({
        url: "/v1/calls",
        method: "POST",
        data: callData,
      }),
    }),
    createOrganisation: builder.mutation<Organisation, OrganisationFormData>({
      query: (organisation) => ({
        url: "/v1/organisations",
        method: "POST",
        data: organisation,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Organisations", "Home"]),
    }),
    getOrganisations: builder.query<Organisation[], void>({
      query: () => ({
        url: `/v1/organisations`,
        method: "get",
      }),
      providesTags: ["Organisations"],
    }),
    getOrganisation: builder.query<Organisation, string>({
      query: (organisationId) => ({
        url: `/v1/organisations/${organisationId}`,
        method: "get",
      }),
      providesTags: ["Organisations"],
    }),
    getOwnPhoneNumbers: builder.query<PhoneNumber[], void>({
      query: () => ({
        url: `/v1/phone-numbers`,
        method: "get",
      }),
      providesTags: ["PhoneNumbers"],
    }),
    updatePhoneNumber: builder.mutation<PhoneNumber, { id: string; alias?: string }>({
      query: ({ id, ...data }) => ({
        url: `/v1/phone-numbers/${id}`,
        method: "put",
        data: {
          alias: data.alias,
        },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PhoneNumbers"]),
    }),
    assignCallFlowToPhoneNumber: builder.mutation<PhoneNumber, { id: string; callFlowId: string | null }>({
      query: ({ id, callFlowId }) => ({
        url: `/v1/phone-numbers/${id}/call-flow`,
        method: "put",
        data: { callFlowId },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PhoneNumbers", "CallFlows"]),
    }),
    purchasePhoneNumber: builder.mutation<PhoneNumber, PurchasePhoneNumberRequest>({
      query: (data) => ({
        url: "/v1/phone-numbers",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PhoneNumbers", "Billing", "Organisations", "CallFlows"]),
    }),
    submitTollfreeVerification: builder.mutation<PhoneNumber, { phoneNumberId: string; optInDescription?: string }>({
      query: ({ phoneNumberId, ...data }) => ({
        url: `/v1/phone-numbers/${phoneNumberId}/tollfree-verification`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PhoneNumbers"]),
    }),
    refreshTollfreeVerification: builder.mutation<PhoneNumber, string>({
      query: (phoneNumberId) => ({
        url: `/v1/phone-numbers/${phoneNumberId}/tollfree-verification/refresh`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PhoneNumbers"]),
    }),
    releasePhoneNumber: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/phone-numbers/${id}`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PhoneNumbers", "CallFlows", "Billing"]),
    }),
    getAvailablePhoneNumbers: builder.query<AvailablePhoneNumber[], PhoneNumberSearchParams>({
      query: (searchParams) => ({
        params: searchParams,
        url: `/v1/phone-numbers/available`,
        method: "get",
      }),
      providesTags: ["AvailablePhoneNumbers"],
    }),
    getBillingOverview: builder.query<BillingOverview, void>({
      query: () => ({
        url: `/v1/billing`,
        method: "get",
      }),
      providesTags: ["Billing", "PaymentMethods", "Deposits"],
    }),
    getReferrals: builder.query<ReferralOverview, void>({
      query: () => ({
        url: `/v1/referrals`,
        method: "get",
      }),
      providesTags: ["Referrals"],
    }),
    getBillingUsage: builder.query<BillingUsage, void>({
      query: () => ({
        url: `/v1/billing/usage`,
        method: "get",
      }),
      providesTags: ["Billing"],
    }),
    createPaymentIntent: builder.mutation<PaymentIntentData, { amount: number }>({
      query: (data) => ({
        url: `/v1/billing/payment-intent`,
        method: "post",
        data,
      }),
    }),
    getPaymentMethods: builder.query<PaymentMethodData[], void>({
      query: () => ({
        url: `/v1/billing/payment-methods`,
        method: "get",
      }),
      providesTags: ["PaymentMethods"],
    }),
    attachPaymentMethod: builder.mutation<PaymentMethodData, { paymentMethodId: string; setAsDefault: boolean }>({
      query: (data) => ({
        url: `/v1/billing/payment-methods`,
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PaymentMethods", "Billing"]),
    }),
    setDefaultPaymentMethod: builder.mutation<PaymentMethodData, string>({
      query: (paymentMethodId) => ({
        url: `/v1/billing/payment-methods/${paymentMethodId}/default`,
        method: "put",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PaymentMethods", "Billing"]),
    }),
    detachPaymentMethod: builder.mutation<void, string>({
      query: (paymentMethodId) => ({
        url: `/v1/billing/payment-methods/${paymentMethodId}`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PaymentMethods", "Billing"]),
    }),
    getPlans: builder.query<SubscriptionPlan[], void>({
      query: () => ({
        url: `/v1/billing/plans`,
        method: "get",
      }),
      providesTags: ["Billing"],
    }),
    getSupportChatToken: builder.query<SupportChatToken | null, void>({
      query: () => ({
        url: `/v1/support/chat-token`,
        method: "get",
      }),
    }),
    getAddOns: builder.query<AddOn[], void>({
      query: () => ({
        url: `/v1/billing/add-ons`,
        method: "get",
      }),
      providesTags: ["Billing"],
    }),
    getAddOnPrices: builder.query<OrgAddOnPriceEntry[], void>({
      query: () => ({
        url: `/v1/billing/add-ons/prices`,
        method: "get",
      }),
      providesTags: ["Billing"],
    }),
    getAiListingCharges: builder.query<AiListingCharge[], void>({
      query: () => ({
        url: `/v1/billing/ai-listing-charges`,
        method: "get",
      }),
      providesTags: ["Billing", "AiAgents"],
    }),
    setAddOn: builder.mutation<AddOn, { addOnType: string; quantity: number }>({
      query: (data) => ({
        url: `/v1/billing/add-ons`,
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing"]),
    }),
    createSubscription: builder.mutation<
      StripeSubscriptionData,
      { plan: string; paymentMethodId: string; interval?: string }
    >({
      query: (data) => ({
        url: `/v1/billing/subscription`,
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing", "Subscription", "Home"]),
    }),
    updateSubscription: builder.mutation<StripeSubscriptionData, { plan: string; interval?: string }>({
      query: (data) => ({
        url: `/v1/billing/subscription`,
        method: "put",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing", "Subscription", "Home"]),
    }),
    cancelSubscription: builder.mutation<StripeSubscriptionData, void>({
      query: () => ({
        url: `/v1/billing/subscription`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing", "Subscription", "Home"]),
    }),
    keepSubscription: builder.mutation<StripeSubscriptionData, void>({
      query: () => ({
        url: `/v1/billing/subscription/keep`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing", "Subscription", "Home"]),
    }),
    getSubscription: builder.query<StripeSubscriptionData, void>({
      query: () => ({
        url: `/v1/billing/subscription`,
        method: "get",
      }),
      providesTags: ["Subscription"],
    }),
    getSubscriptionPreview: builder.query<SubscriptionPreview, { plan: string; interval: BillingInterval }>({
      query: (data) => ({
        url: `/v1/billing/subscription/preview`,
        method: "post",
        data,
      }),
      providesTags: ["Subscription", "BillingDetails"],
    }),
    createIdentityVerificationSession: builder.mutation<IdentityVerificationData, void>({
      query: () => ({
        url: `/v1/billing/kyc/verification-session`,
        method: "post",
      }),
    }),
    setupAutoTopUp: builder.mutation<Organisation, { enabled: boolean; amount?: number; threshold?: number }>({
      query: (data) => ({
        url: `/v1/billing/auto-topup`,
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Billing", "Home"]),
    }),
    getDeposits: builder.query<Deposit[], void>({
      query: () => ({
        url: `/v1/billing/deposits`,
        method: "get",
      }),
      providesTags: ["Deposits"],
    }),
    getCharges: builder.query<PagedResponse<Charge>, { page?: number; size?: number; type?: ChargeType }>({
      query: ({ page = 0, size = 25, type }) => ({
        url: `/v1/billing/charges`,
        method: "get",
        params: { page, size, sort: "createdAt,desc", ...(type ? { type } : {}) },
      }),
      providesTags: ["Charges"],
    }),
    getCharge: builder.query<Charge, string>({
      query: (chargeId) => ({
        url: `/v1/billing/charges/${chargeId}`,
        method: "get",
      }),
      providesTags: ["Charges"],
    }),
    getBillingDetails: builder.query<BillingDetails, void>({
      query: () => ({
        url: `/v1/billing/details`,
        method: "get",
      }),
      providesTags: ["BillingDetails"],
    }),
    updateBillingDetails: builder.mutation<BillingDetails, BillingDetails>({
      query: (data) => ({
        url: `/v1/billing/details`,
        method: "put",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["BillingDetails", "Billing"]),
    }),
    getInvoices: builder.query<PagedResponse<Invoice>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 25 }) => ({
        url: `/v1/billing/invoices`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["Invoices"],
    }),
    getInvoice: builder.query<Invoice, string>({
      query: (invoiceId) => ({
        url: `/v1/billing/invoices/${invoiceId}`,
        method: "get",
      }),
      providesTags: ["Invoices"],
    }),
    getInvoicePdfLink: builder.query<InvoicePdfLink, string>({
      query: (invoiceId) => ({
        url: `/v1/billing/invoices/${invoiceId}/pdf`,
        method: "get",
      }),
      keepUnusedDataFor: 0,
    }),
    getCallFlows: builder.query<CallFlow[], void>({
      query: () => ({
        url: "/v1/call-flows",
        method: "get",
      }),
      providesTags: ["CallFlows"],
    }),

    getCallFlow: builder.query<CallFlow, string>({
      query: (id) => ({
        url: `/v1/call-flows/${id}`,
        method: "get",
      }),
      providesTags: ["CallFlows"],
    }),

    createCallFlow: builder.mutation<CallFlow, CreateCallFlowRequest>({
      query: (data) => ({
        url: "/v1/call-flows",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows", "CallFlowRevisions"]),
    }),
    updateCallFlow: builder.mutation<CallFlow, { id: string; data: UpdateCallFlowRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/call-flows/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows", "CallFlowRevisions"]),
    }),
    getCallFlowRevisions: builder.query<CallFlowRevision[], string>({
      query: (id) => ({
        url: `/v1/call-flows/${id}/revisions`,
        method: "get",
      }),
      providesTags: ["CallFlowRevisions"],
    }),
    restoreCallFlowRevision: builder.mutation<CallFlow, { id: string; revisionId: string }>({
      query: ({ id, revisionId }) => ({
        url: `/v1/call-flows/${id}/revisions/${revisionId}/restore`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows", "CallFlowRevisions"]),
    }),
    deleteCallFlow: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/call-flows/${id}`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows", "PhoneNumbers"]),
    }),
    duplicateCallFlow: builder.mutation<CallFlow, string>({
      query: (id) => ({
        url: `/v1/call-flows/${id}/duplicate`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows"]),
    }),
    toggleCallFlow: builder.mutation<CallFlow, string>({
      query: (id) => ({
        url: `/v1/call-flows/${id}/toggle`,
        method: "patch",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CallFlows"]),
    }),
    initiateCall: builder.mutation<Call, InitiateCallRequest>({
      query: ({ acknowledgeQuietHours, acknowledgeContactWarning, suppressContactWarning, dncCheck, ...data }) => ({
        url: `/v1/calls`,
        method: "post",
        data,
        // The endpoint reads the acknowledgement from the query string: the body is the Call
        // entity, and a per-request compliance acknowledgement does not belong on it.
        params: {
          ...(acknowledgeQuietHours ? { acknowledgeQuietHours: true } : {}),
          ...(acknowledgeContactWarning ? { acknowledgeContactWarning: true } : {}),
          ...(suppressContactWarning ? { suppressContactWarning: true } : {}),
          ...(dncCheck ? { dncCheck } : {}),
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls"]),
    }),
    endCall: builder.mutation<Call, string>({
      query: (id) => ({
        url: `/v1/calls/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls"]),
    }),
    archiveCall: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/archive`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "CommunicationItems"]),
    }),
    restoreCall: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/restore`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "CommunicationItems"]),
    }),
    deleteCallPermanently: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/permanent`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "CommunicationItems"]),
    }),
    holdCall: builder.mutation<Call, string>({
      query: (id) => ({ url: `/v1/calls/${id}/hold`, method: "post" }),
      invalidatesTags: (result, error) => (error ? [] : ["Calls"]),
    }),
    reportCallPlatform: builder.mutation<void, { callId: string; data: CallPlatformRequest }>({
      query: ({ callId, data }) => ({ url: `/v1/calls/${callId}/platform`, method: "put", data }),
    }),
    getCallParticipants: builder.query<CallParticipant[], string>({
      query: (callId) => ({ url: `/v1/calls/${callId}/participants`, method: "get" }),
      providesTags: (_, __, callId) => [{ type: "CallParticipants", id: callId }],
    }),
    addCallParticipant: builder.mutation<CallParticipant[], { callId: string; data: AddCallParticipantRequest }>({
      query: ({ callId, data }) => ({ url: `/v1/calls/${callId}/participants`, method: "post", data }),
      invalidatesTags: (_, error, { callId }) => (error ? [] : [{ type: "CallParticipants", id: callId }]),
    }),
    holdCallParticipant: builder.mutation<CallParticipant[], { callId: string; participantId: string }>({
      query: ({ callId, participantId }) => ({
        url: `/v1/calls/${callId}/participants/${participantId}/hold`,
        method: "post",
      }),
      invalidatesTags: (_, error, { callId }) => (error ? [] : [{ type: "CallParticipants", id: callId }]),
    }),
    resumeCallParticipant: builder.mutation<CallParticipant[], { callId: string; participantId: string }>({
      query: ({ callId, participantId }) => ({
        url: `/v1/calls/${callId}/participants/${participantId}/resume`,
        method: "post",
      }),
      invalidatesTags: (_, error, { callId }) => (error ? [] : [{ type: "CallParticipants", id: callId }]),
    }),
    endCallParticipant: builder.mutation<CallParticipant[], { callId: string; participantId: string }>({
      query: ({ callId, participantId }) => ({
        url: `/v1/calls/${callId}/participants/${participantId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error, { callId }) => (error ? [] : [{ type: "CallParticipants", id: callId }]),
    }),
    transferCall: builder.mutation<CallTransfer, { callId: string; data: CallTransferRequest }>({
      query: ({ callId, data }) => ({ url: `/v1/calls/${callId}/transfers`, method: "post", data }),
      invalidatesTags: (_, error, { callId }) => (error ? [] : [{ type: "CallParticipants", id: callId }, "Calls"]),
    }),
    getMemberAvailability: builder.query<Membership[], void>({
      query: () => ({ url: "/v1/memberships/availability", method: "get" }),
      providesTags: ["MemberAvailability"],
    }),
    getCallBySid: builder.query<Call, string>({
      query: (sid) => ({
        url: `/v1/calls/by-sid/${sid}`,
        method: "get",
      }),
    }),
    getTelephonySettings: builder.query<TelephonySettings, void>({
      query: () => ({
        url: "/v1/telephony/settings",
        method: "get",
        params: { platform: DEVICE_PLATFORM },
      }),
    }),
    updateCallerId: builder.mutation<void, { phoneNumberId: string }>({
      query: ({ phoneNumberId }) => ({
        url: "/v1/telephony/caller-id",
        method: "put",
        data: { phoneNumberId },
      }),
    }),
    updateLineTypeIntelligence: builder.mutation<void, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: "/v1/telephony/line-type-intelligence",
        method: "put",
        data: { value: settingState(enabled) },
      }),
    }),
    updateMachineDetection: builder.mutation<void, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: "/v1/telephony/machine-detection",
        method: "put",
        data: { value: settingState(enabled) },
      }),
    }),
    updateTranscription: builder.mutation<void, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: "/v1/telephony/transcription",
        method: "put",
        data: { value: settingState(enabled) },
      }),
    }),
    updateCallSummaries: builder.mutation<void, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: "/v1/telephony/call-summaries",
        method: "put",
        data: { value: settingState(enabled) },
      }),
    }),
    updateBusyTeammateRinging: builder.mutation<void, { value: BusyTeammateRinging }>({
      query: ({ value }) => ({
        url: "/v1/telephony/busy-teammates",
        method: "put",
        data: { value },
      }),
    }),
    createCallSummaryTask: builder.mutation<Task, string>({
      query: (callId) => ({
        url: `/v1/calls/${callId}/summary-task`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "Tasks"]),
    }),
    getCall: builder.query<Call, string>({
      query: (id) => ({
        url: `/v1/calls/${id}`,
        method: "get",
      }),
      providesTags: ["Calls"],
    }),
    getIncomingCall: builder.query<IncomingCallEvent, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/incoming`,
        method: "get",
      }),
    }),
    getCalls: builder.query<
      PagedResponse<Call>,
      {
        page?: number;
        size?: number;
        direction?: string;
        status?: string;
        q?: string;
        from?: string;
        to?: string;
        dispositionId?: string;
        hasDisposition?: boolean;
        unreviewedRecording?: boolean;
        missedWithoutCallback?: boolean;
        archived?: boolean;
      }
    >({
      query: ({ page = 0, size = 50, direction, status, q, from, to, dispositionId, hasDisposition, unreviewedRecording, missedWithoutCallback, archived } = {}) => ({
        url: `/v1/calls`,
        method: "get",
        params: { page, size, direction, status, q, from, to, dispositionId, hasDisposition, unreviewedRecording, missedWithoutCallback, archived },
      }),
      providesTags: ["Calls"],
    }),
    getCallStats: builder.query<CallStats, { from?: string; to?: string }>({
      query: (params) => ({
        url: `/v1/calls/stats`,
        method: "get",
        params,
      }),
      providesTags: ["Calls"],
    }),
    getCallRecordingUrl: builder.query<{ url: string }, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/recording-url`,
        method: "get",
      }),
    }),
    markCallRecordingReviewed: builder.mutation<Call, string>({
      query: (id) => ({
        url: `/v1/calls/${id}/recording/reviewed`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "CommunicationItems"]),
    }),
    setCallRecordingState: builder.mutation<void, { id: string; action: RecordingAction }>({
      query: ({ id, action }) => ({
        url: `/v1/calls/${id}/recording/${action}`,
        method: "post",
      }),
    }),
    getActiveCall: builder.query<Call, void>({
      query: () => ({
        url: "/v1/calls/active",
        method: "get",
      }),
    }),
    getOwnMembership: builder.query<Membership, void>({
      query: () => ({
        url: "/v1/memberships/me",
        method: "get",
      }),
      providesTags: ["Memberships"],
    }),
    updateOwnVoicemailGreeting: builder.mutation<Membership, VoicemailGreetingRequest>({
      query: (data) => ({
        url: "/v1/memberships/me/voicemail-greeting",
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships"]),
    }),
    updateOwnContactWarningPreference: builder.mutation<Membership, ContactWarningPreferenceRequest>({
      query: (data) => ({
        url: "/v1/memberships/me/contact-warning-preference",
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships"]),
    }),
    getMembers: builder.query<Membership[], { status?: string }>({
      query: (params) => ({
        url: "/v1/memberships",
        method: "get",
        params,
      }),
      providesTags: ["Memberships"],
    }),
    inviteMember: builder.mutation<Membership, InviteMemberRequest>({
      query: (data) => ({
        url: "/v1/memberships/invite",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships"]),
    }),
    acceptInvitation: builder.mutation<Membership, string>({
      query: (token) => ({
        url: `/v1/memberships/accept/${token}`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships", "Organisations"]),
    }),
    updateMemberRole: builder.mutation<Membership, { id: string; data: UpdateMemberRoleRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/memberships/${id}/role`,
        method: "patch",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships"]),
    }),
    assignDialerSeat: builder.mutation<Membership, { id: string; data: DialerSeatRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/memberships/${id}/dialer-seat`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships", "Home"]),
    }),
    setExportAccess: builder.mutation<Membership, { id: string; data: ExportAccessRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/memberships/${id}/export-access`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships", "Home"]),
    }),
    setMemberDisplayName: builder.mutation<Membership, { id: string; data: DisplayNameRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/memberships/${id}/display-name`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships", "Home", "Teams", "MemberAvailability"]),
    }),
    updateOwnDisplayName: builder.mutation<Membership, DisplayNameRequest>({
      query: (data) => ({
        url: "/v1/memberships/me/display-name",
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Memberships", "Home", "Teams", "MemberAvailability"]),
    }),
    removeMember: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/memberships/${id}`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships"]),
    }),
    leaveOrganisation: builder.mutation<void, void>({
      query: () => ({
        url: "/v1/memberships/leave",
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships", "Organisations"]),
    }),
    resendInvitation: builder.mutation<Membership, string>({
      query: (id) => ({
        url: `/v1/memberships/${id}/resend`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Memberships"]),
    }),
    getContacts: builder.query<
        PagedResponse<Contact>,
        {
          page?: number;
          size?: number;
          sort?: string;
          q?: string;
          company?: string;
          hasPhone?: boolean;
          listIds?: string[];
          tagIds?: string[];
          type?: ContactType;
          excludeType?: ContactType;
          archived?: boolean;
          importRunId?: string;
          importAction?: string;
          dealStageId?: string;
          withoutDeal?: boolean;
        }
    >({
      query: ({ page = 0, size = 20, sort = "createdAt,desc", q, company, hasPhone, listIds, tagIds, type, excludeType, archived, importRunId, importAction, dealStageId, withoutDeal }) => ({
        url: "/v1/contacts",
        method: "get",
        params: { page, size, sort, q, company, hasPhone, listIds, tagIds, type, excludeType, archived, importRunId, importAction, dealStageId, withoutDeal },
      }),
      providesTags: ["Contacts"],
    }),
    getContact: builder.query<Contact, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}`,
        method: "get",
      }),
      providesTags: (_result, _error, id) => [{ type: "Contacts", id }],
    }),
    getLeadSummary: builder.query<LeadSummary, void>({
      query: () => ({
        url: "/v1/contacts/lead-summary",
        method: "get",
      }),
      providesTags: ["LeadSummary"],
    }),
    promoteContact: builder.mutation<Deal, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/promote`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "Deals", "LeadSummary", "DealActivities"]),
    }),
    convertContact: builder.mutation<Deal, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/convert`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "Deals", "LeadSummary", "DealActivities"]),
    }),
    undoConversion: builder.mutation<Contact, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/undo-conversion`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "Deals", "LeadSummary", "DealActivities"]),
    }),
    createContact: builder.mutation<Contact, Partial<Contact>>({
      query: (data) => ({
        url: "/v1/contacts",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Contacts"]),
    }),
    updateContact: builder.mutation<Contact, { id: string; data: Partial<Contact> }>({
      query: ({ id, data }) => ({
        url: `/v1/contacts/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Contacts"]),
    }),
    archiveContact: builder.mutation<Contact, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/archive`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Contacts", "Conversations", "CommunicationItems", "Deals", "Tasks", "Sites"]),
    }),
    restoreContact: builder.mutation<Contact, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/restore`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Contacts", "Conversations", "CommunicationItems", "Deals", "Tasks", "Sites"]),
    }),
    deleteContactPermanently: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/permanent`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Contacts", "Conversations", "CommunicationItems", "Calls", "Messages", "Deals", "Tasks", "Sites"]),
    }),
    getContactDuplicates: builder.query<Contact[], string>({
      query: (id) => ({
        url: `/v1/contacts/${id}/duplicates`,
        method: "get",
      }),
      providesTags: ["Contacts"],
    }),
    mergeContacts: builder.mutation<Contact, { id: string; sourceId: string }>({
      query: ({ id, sourceId }) => ({
        url: `/v1/contacts/${id}/merge`,
        method: "post",
        data: { id: sourceId },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : ["Contacts", "Conversations", "CommunicationItems", "Calls", "Messages", "Deals", "Tasks"],
    }),
    addCampaignContacts: builder.mutation<CampaignContact[], { campaignId: string; contactIds: string[] }>({
      query: ({ campaignId, contactIds }) => ({
        url: `/v1/campaigns/${campaignId}/contacts`,
        method: "post",
        data: { contactIds },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    getCampaignContacts: builder.query<CampaignContact[], string>({
      query: (campaignId) => ({
        url: `/v1/power-dialer/campaigns/${campaignId}/contacts`,
        method: "get",
      }),
      providesTags: ["CampaignContacts"],
    }),
    getCampaignContactIds: builder.query<string[], string>({
      query: (campaignId) => ({
        url: `/v1/campaigns/${campaignId}/contact-ids`,
        method: "get",
      }),
      providesTags: ["CampaignContacts"],
    }),
    getSequenceStats: builder.query<SequenceStats, string>({
      query: (campaignId) => ({
        url: `/v1/campaigns/${campaignId}/sequence-stats`,
        method: "get",
      }),
      providesTags: ["CampaignContacts"],
    }),
    getCampaignEnrollments: builder.query<
      PagedResponse<SequenceEnrollment>,
      { campaignId: string; page?: number; size?: number }
    >({
      query: ({ campaignId, ...params }) => ({
        url: `/v1/campaigns/${campaignId}/enrollments`,
        method: "get",
        params,
      }),
      providesTags: ["CampaignContacts"],
    }),
    removeCampaignContacts: builder.mutation<void, { campaignId: string; contactIds: string[] }>({
      query: ({ campaignId, contactIds }) => ({
        url: `/v1/campaigns/${campaignId}/contacts`,
        method: "delete",
        data: { contactIds },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["CampaignContacts", "Campaigns"]),
    }),
    getCampaigns: builder.query<Campaign[], void>({
      query: () => ({
        url: "/v1/campaigns",
        method: "get",
      }),
      providesTags: ["Campaigns"],
    }),
    getCampaignAudiencePreview: builder.query<CampaignAudiencePreview, CampaignAudienceRequest>({
      query: (data) => ({
        url: "/v1/campaigns/audience-preview",
        method: "post",
        data,
      }),
    }),
    getContactImports: builder.query<ContactImportSummary[], void>({
      query: () => ({
        url: "/v1/contact-imports",
        method: "get",
      }),
    }),

    getCampaign: builder.query<Campaign, string>({
      query: (id) => ({
        url: `/v1/campaigns/${id}`,
        method: "get",
      }),
      providesTags: ["Campaigns"],
    }),

    createCampaign: builder.mutation<Campaign, CampaignFormData>({
      query: (data) => ({
        url: "/v1/campaigns",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),

    updateCampaign: builder.mutation<Campaign, { id: string; data: CampaignFormData }>({
      query: ({ id, data }) => ({
        url: `/v1/campaigns/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),

    deleteCampaign: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/campaigns/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),
    archiveCampaign: builder.mutation<Campaign, string>({
      query: (id) => ({
        url: `/v1/campaigns/${id}/archive`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),
    unarchiveCampaign: builder.mutation<Campaign, string>({
      query: (id) => ({
        url: `/v1/campaigns/${id}/unarchive`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),
    startCampaign: builder.mutation<
      Campaign,
      { campaign: { id: string }; acknowledgedRegistration: boolean }
    >({
      query: ({ campaign, acknowledgedRegistration }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/start`,
        method: "post",
        data: { acknowledgedRegistration },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns", "CampaignContacts"]),
    }),
    getCampaignCompliancePreflight: builder.query<CampaignCompliancePreflight, string>({
      query: (id) => ({
        url: `/v1/power-dialer/campaigns/${id}/compliance-preflight`,
        method: "get",
      }),
      providesTags: ["CampaignCompliance"],
    }),
    checkCampaignDncNumbers: builder.mutation<CampaignCompliancePreflight, string>({
      query: (id) => ({
        url: `/v1/power-dialer/campaigns/${id}/dnc-check`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignCompliance", "Billing"]),
    }),
    checkQuietHours: builder.query<QuietHoursCheckResult, string>({
      query: (phoneNumber) => ({
        url: `/v1/compliance/quiet-hours?phoneNumber=${encodeURIComponent(phoneNumber)}`,
        method: "get",
      }),
    }),
    getQuietHoursPolicy: builder.query<QuietHoursPolicySetting, void>({
      query: () => ({
        url: `/v1/compliance/quiet-hours/policy`,
        method: "get",
      }),
      providesTags: ["QuietHoursPolicy"],
    }),
    updateQuietHoursPolicy: builder.mutation<QuietHoursPolicySetting, { policy: string }>({
      query: ({ policy }) => ({
        url: `/v1/compliance/quiet-hours/policy`,
        method: "put",
        data: { value: policy },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["QuietHoursPolicy"]),
    }),
    getOptOutKeywords: builder.query<OptOutKeywordSetting, void>({
      query: () => ({
        url: `/v1/compliance/opt-out-keywords`,
        method: "get",
      }),
      providesTags: ["OptOutKeywords"],
    }),
    addOptOutKeyword: builder.mutation<SmsOptOutKeyword, { keyword: string }>({
      query: (data) => ({
        url: `/v1/compliance/opt-out-keywords`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OptOutKeywords"]),
    }),
    removeOptOutKeyword: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/compliance/opt-out-keywords/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OptOutKeywords"]),
    }),
    getSmsEligibility: builder.query<SmsEligibility, void>({
      query: () => ({
        url: `/v1/compliance/sms-eligibility`,
        method: "get",
      }),
      providesTags: ["SmsEligibility"],
    }),
    getA2pPricing: builder.query<A2pPricingRates, void>({
      query: () => ({
        url: `/v1/compliance/a2p-pricing`,
        method: "get",
      }),
    }),
    stopCampaign: builder.mutation<Campaign, { campaign: { id: string } }>({
      query: ({ campaign }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/stop`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns", "CampaignContacts"]),
    }),

    startAiCampaign: builder.mutation<Campaign, { id: string; acknowledgedRegistration: boolean }>({
      query: ({ id, acknowledgedRegistration }) => ({
        url: `/v1/campaigns/${id}/start`,
        method: "post",
        data: { acknowledgedRegistration },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns", "CampaignContacts"]),
    }),
    getAiCampaignCompliancePreflight: builder.query<CampaignCompliancePreflight, string>({
      query: (id) => ({
        url: `/v1/campaigns/${id}/compliance-preflight`,
        method: "get",
      }),
    }),

    pauseAiCampaign: builder.mutation<Campaign, string>({
      query: (id) => ({ url: `/v1/campaigns/${id}/pause`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns"]),
    }),

    stopAiCampaign: builder.mutation<Campaign, string>({
      query: (id) => ({ url: `/v1/campaigns/${id}/stop`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Campaigns", "CampaignContacts"]),
    }),

    getAiCampaignLive: builder.query<AiCampaignLiveResponse, string>({
      query: (id) => ({ url: `/v1/campaigns/${id}/live`, method: "get" }),
      providesTags: ["CampaignContacts"],
    }),

    addCampaignContactsByPhone: builder.mutation<
      CampaignContact[],
      { campaignId: string; phoneNumbers: string[] }
    >({
      query: ({ campaignId, phoneNumbers }) => ({
        url: `/v1/campaigns/${campaignId}/contacts/phones`,
        method: "post",
        data: { phoneNumbers },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    holdCampaignCall: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/hold/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    resumeCampaignCall: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/resume/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    dropCampaignVoicemail: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/voicemail-drop/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    endCampaignCall: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/end/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts"]),
    }),
    setCampaignDisposition: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string }; disposition: string; notes?: string }
    >({
      query: ({ campaign, campaignContact, ...data }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/disposition/${campaignContact.id}`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ["CampaignContacts", "DialerQueue", "Contacts", "DncEntries", "Deals", "Tasks", "CommunicationItems"],
    }),
    saveCampaignNotes: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string }; callId?: string; notes: string }
    >({
      query: ({ campaign, campaignContact, notes }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/notes/${campaignContact.id}`,
        method: "put",
        data: { notes },
      }),
      async onQueryStarted(arg, lifecycle) {
        if (!arg.callId) {
          return;
        }
        const patches = patchCallInAllCaches(restApi, lifecycle, arg.callId, (call) => {
          call.notes = arg.notes;
        });
        await undoOnError(lifecycle.queryFulfilled, patches);
      },
      invalidatesTags: (_, error) => (error ? [] : ["Calls", "CommunicationItems"]),
    }),
    startDialerSession: builder.mutation<DialerSession, { campaign: { id: string } }>({
      query: ({ campaign }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/sessions`,
        method: "post",
      }),
    }),
    endDialerSession: builder.mutation<DialerSession, { sessionId: string; reason?: DialerSessionEndReason }>({
      query: ({ sessionId, reason }) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/end`,
        method: "post",
        params: reason ? { reason } : undefined,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerSessions"]),
    }),
    getDialerSession: builder.query<DialerSession, string>({
      query: (sessionId) => ({
        url: `/v1/power-dialer/sessions/${sessionId}`,
        method: "get",
      }),
      providesTags: ["DialerSessions"],
    }),
    getDialerSessionSnapshot: builder.query<DialerSessionSnapshot, string>({
      query: (sessionId) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/snapshot`,
        method: "get",
      }),
      providesTags: ["DialerSessions"],
    }),
    markDialerSessionReady: builder.mutation<DialerSession, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/ready`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerSessions"]),
    }),
    startDialerSessionBreak: builder.mutation<DialerSession, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/break`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerSessions"]),
    }),
    takeDialerReturnCall: builder.mutation<DialerReturnCallInfo, { sessionId: string; returnCallId: string }>({
      query: ({ sessionId, returnCallId }) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/return-calls/${returnCallId}/take`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerSessions", "Calls"]),
    }),
    hangUpDialerReturnCall: builder.mutation<void, { sessionId: string; returnCallId: string }>({
      query: ({ sessionId, returnCallId }) => ({
        url: `/v1/power-dialer/sessions/${sessionId}/return-calls/${returnCallId}/hang-up`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Calls"]),
    }),
    getDialerQueue: builder.query<
      PagedResponse<CampaignContact>,
      { campaignId: string; group: DialerQueueGroup; page: number; size: number }
    >({
      query: ({ campaignId, group, page, size }) => ({
        url: `/v1/power-dialer/campaigns/${campaignId}/queue`,
        method: "get",
        params: { group, page, size },
      }),
      providesTags: ["DialerQueue"],
    }),
    getDialerQueueCounts: builder.query<DialerQueueCounts, string>({
      query: (campaignId) => ({
        url: `/v1/power-dialer/campaigns/${campaignId}/queue/counts`,
        method: "get",
      }),
      providesTags: ["DialerQueue"],
    }),
    markLeadDoNotCall: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/do-not-call/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DncEntries", "Contacts", "CommunicationItems"]),
    }),
    markLeadWrongNumber: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/wrong-number/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "CommunicationItems"]),
    }),
    previewDialNext: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact?: { id: string }; acknowledgedCallingRules?: boolean }
    >({
      query: ({ campaign, campaignContact, acknowledgedCallingRules }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/dial-next`,
        method: "post",
        data: campaignContact
          ? { campaignContactId: campaignContact.id, acknowledgedCallingRules: acknowledgedCallingRules ?? false }
          : {},
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts", "DialerQueue", "DialerPreview"]),
    }),
    skipCampaignContact: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string } }
    >({
      query: ({ campaign, campaignContact }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/skip/${campaignContact.id}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts", "DialerQueue", "DialerPreview"]),
    }),
    tryCampaignLeadNumber: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string }; phoneNumber: string }
    >({
      query: ({ campaign, campaignContact, phoneNumber }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/leads/${campaignContact.id}/try-number`,
        method: "post",
        data: { phoneNumber },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerQueue"]),
    }),
    scheduleLeadCallback: builder.mutation<
      CampaignContact,
      { campaign: { id: string }; campaignContact: { id: string }; callback: CallbackRequest }
    >({
      query: ({ campaign, campaignContact, callback }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/leads/${campaignContact.id}/callback`,
        method: "post",
        data: callback,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts", "DialerQueue"]),
    }),
    requeueCampaignOutcomes: builder.mutation<number, { campaign: { id: string }; requeue: RequeueRequest }>({
      query: ({ campaign, requeue }) => ({
        url: `/v1/power-dialer/campaigns/${campaign.id}/requeue`,
        method: "post",
        data: requeue,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CampaignContacts", "DialerQueue", "DialerPreview"]),
    }),
    getDialerNumbers: builder.query<DialerNumbersOverview, void>({
      query: () => ({
        url: "/v1/power-dialer/numbers",
        method: "get",
      }),
      providesTags: ["DialerNumbers"],
    }),
    setDialerNumberDailyCap: builder.mutation<DialerNumbersOverview, number>({
      query: (dailyCap) => ({
        url: "/v1/power-dialer/numbers/daily-cap",
        method: "put",
        data: { dailyCap },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerNumbers"]),
    }),
    setDialerReturnCallDays: builder.mutation<DialerNumbersOverview, number>({
      query: (days) => ({
        url: "/v1/power-dialer/numbers/return-calls",
        method: "put",
        data: { days },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerNumbers"]),
    }),
    restDialerNumber: builder.mutation<DialerNumbersOverview, string>({
      query: (phoneNumberId) => ({
        url: `/v1/power-dialer/numbers/${phoneNumberId}/rest`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerNumbers"]),
    }),
    restoreDialerNumber: builder.mutation<DialerNumbersOverview, string>({
      query: (phoneNumberId) => ({
        url: `/v1/power-dialer/numbers/${phoneNumberId}/restore`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DialerNumbers"]),
    }),
    getVoicemailDropStatus: builder.query<VoicemailDropStatus, { campaignId: string; campaignContactId: string }>({
      query: ({ campaignId, campaignContactId }) => ({
        url: `/v1/power-dialer/campaigns/${campaignId}/leads/${campaignContactId}/voicemail-drop`,
        method: "get",
      }),
    }),
    getPreviewLead: builder.query<CampaignContact | null, string>({
      query: (campaignId) => ({
        url: `/v1/power-dialer/campaigns/${campaignId}/preview`,
        method: "get",
      }),
      providesTags: ["DialerPreview"],
    }),
    getDialerReport: builder.query<DialerReport, DialerReportFilter>({
      query: (params) => ({
        url: "/v1/power-dialer/reports",
        method: "get",
        params,
      }),
    }),
    getDialerSessionHistory: builder.query<DialerSessionHistoryRow[], DialerReportFilter>({
      query: ({ from, to, campaignId, agentId }) => ({
        url: "/v1/power-dialer/reports/sessions",
        method: "get",
        params: { from, to, campaignId, agentId },
      }),
    }),
    getAbandonmentReport: builder.query<AbandonmentReport, string>({
      query: (campaignId) => ({
        url: `/v1/power-dialer/reports/abandonment/${campaignId}`,
        method: "get",
      }),
    }),
    getLiveMonitorCalls: builder.query<LiveMonitorEntry[], void>({
      query: () => ({
        url: `/v1/monitor/live`,
        method: "get",
      }),
    }),
    startMonitor: builder.mutation<
      SupervisorSession,
      { source: "POWER_DIALER" | "CALL"; id: string; mode: "LISTEN" | "WHISPER" | "BARGE" }
    >({
      query: (body) => ({
        url: `/v1/monitor/start`,
        method: "post",
        data: body,
      }),
    }),
    getCallSupervisorSessions: builder.query<SupervisorSession[], string>({
      query: (callId) => ({
        url: `/v1/monitor/calls/${callId}/sessions`,
        method: "get",
      }),
    }),
    getCallFlowSteps: builder.query<CallFlowRunStep[], string>({
      query: (callId) => ({
        url: `/v1/calls/${callId}/flow-steps`,
        method: "get",
      }),
    }),
    previewVoice: builder.mutation<{ audio: string }, { voice: string; text?: string }>({
      query: (body) => ({
        url: `/v1/tts/preview`,
        method: "post",
        data: body,
      }),
    }),
    getAudioClips: builder.query<AudioClip[], AudioLibrary | void>({
      query: (category) => ({
        url: `/v1/audio-clips`,
        method: "get",
        params: category ? { category } : undefined,
      }),
      providesTags: ["AudioClips"],
    }),
    generateAudioClip: builder.mutation<AudioClip, GenerateAudioClipRequest>({
      query: (data) => ({
        url: `/v1/audio-clips/generate`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AudioClips"]),
    }),
    uploadAudioClip: builder.mutation<
      AudioClip,
      { file: Blob; fileName: string; name: string; category?: AudioLibrary; durationSeconds?: number }
    >({
      query: ({ file, fileName, name, category, durationSeconds }) => {
        const formData = new FormData();
        formData.append("file", file, fileName);
        formData.append("name", name);
        if (category) {
          formData.append("category", category);
        }
        if (durationSeconds) {
          formData.append("durationSeconds", String(durationSeconds));
        }
        return {
          url: `/v1/audio-clips`,
          method: "post",
          headers: { "Content-Type": "multipart/form-data;" } as AxiosRequestHeaders,
          data: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ["AudioClips"]),
    }),
    renameAudioClip: builder.mutation<AudioClip, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `/v1/audio-clips/${id}`,
        method: "put",
        data: { name },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AudioClips"]),
    }),
    deleteAudioClip: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/audio-clips/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AudioClips"]),
    }),
    getContactLists: builder.query<
      PagedResponse<ContactList>,
      { page?: number; size?: number; sort?: string; q?: string }
    >({
      query: ({ page = 0, size = 20, sort = "createdAt,desc", q } = {}) => ({
        url: "/v1/contact-lists",
        method: "get",
        params: { page, size, sort, q },
      }),
      providesTags: ["ContactList"],
    }),
    getContactList: builder.query<ContactList, string>({
      query: (id) => ({
        url: `/v1/contact-lists/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "ContactList", id }],
    }),
    createContactList: builder.mutation<ContactList, Partial<ContactList>>({
      query: (data) => ({
        url: "/v1/contact-lists",
        method: "POST",
        data, // axiosBaseQuery uses `data`
      }),
      invalidatesTags: (result, error) => (error ? [] : ["ContactList"]),
    }),
    updateContactList: builder.mutation<ContactList, { id: string; data: Partial<ContactList> }>({
      query: ({ id, data }) => ({
        url: `/v1/contact-lists/${id}`,
        method: "PUT",
        data, // axiosBaseQuery uses `data`
      }),
      invalidatesTags: (result, error, { id }) => (error ? [] : ["ContactList", { type: "ContactList", id }]),
    }),
    deleteContactList: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/contact-lists/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["ContactList"]),
    }),
    skipTraceContact: builder.mutation<SkipTraceJob, { contactId: string; mode: SkipTraceMode }>({
      query: ({ contactId, mode }) => ({
        url: `/v1/contacts/${contactId}/skip-trace`,
        method: "POST",
        params: { mode },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["SkipTraceJobs", "Contacts", "Home", "Billing"]),
    }),
    skipTraceContactList: builder.mutation<SkipTraceJob, { listId: string; mode: SkipTraceMode }>({
      query: ({ listId, mode }) => ({
        url: `/v1/contact-lists/${listId}/skip-trace`,
        method: "POST",
        params: { mode },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["SkipTraceJobs", "Home", "Billing"]),
    }),
    getSkipTracePreview: builder.query<SkipTracePreview, { scope: SkipTraceScope; targetId: string }>({
      query: ({ scope, targetId }) => ({
        url: "/v1/skip-trace/preview",
        method: "GET",
        params: { scope, targetId },
      }),
    }),
    skipTraceSelection: builder.mutation<SkipTraceJob, { contactIds: string[]; mode: SkipTraceMode }>({
      query: ({ contactIds, mode }) => ({
        url: "/v1/skip-trace/bulk",
        method: "POST",
        data: { contactIds, mode },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["SkipTraceJobs", "Home", "Billing"]),
    }),
    getSkipTraceSelectionPreview: builder.query<SkipTracePreview, string[]>({
      query: (contactIds) => ({
        url: "/v1/skip-trace/preview",
        method: "POST",
        data: { contactIds },
      }),
    }),
    getSkipTraceJob: builder.query<SkipTraceJob, number>({
      query: (jobId) => ({
        url: `/v1/skip-trace/jobs/${jobId}`,
        method: "GET",
      }),
      providesTags: ["SkipTraceJobs"],
    }),
    getSkipTraceJobs: builder.query<PagedResponse<SkipTraceJob>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 20 }) => ({
        url: "/v1/skip-trace/jobs",
        method: "GET",
        params: { page, size },
      }),
      providesTags: ["SkipTraceJobs"],
    }),
    getSkipTraceJobResults: builder.query<
      PagedResponse<SkipTraceResult>,
      { jobId: number; page?: number; size?: number }
    >({
      query: ({ jobId, page = 0, size = 50 }) => ({
        url: `/v1/skip-trace/jobs/${jobId}/results`,
        method: "GET",
        params: { page, size },
      }),
      providesTags: ["SkipTraceJobs"],
    }),
    getContactListContacts: builder.query<
      PagedResponse<ContactListContact>,
      { listId: string; page?: number; size?: number; q?: string; sort?: string }
    >({
      query: ({ listId, page = 0, size = 20, q, sort }) => ({
        url: `/v1/contact-lists/${listId}/contacts`,
        method: "GET",
        params: { page, size, q, sort },
      }),
      providesTags: (_r, _e, { listId }) => [{ type: "ContactListContacts", id: listId }],
    }),
    addContactsToList: builder.mutation<any, { listId: string; contactIds: string[] }>({
      query: ({ listId, contactIds }) => ({
        url: `/v1/contact-lists/${listId}/contacts`,
        method: "POST",
        data: { contactIds }, // axiosBaseQuery uses `data`
      }),
      invalidatesTags: (result, error, { listId }) => (error ? [] : [{ type: "ContactListContacts", id: listId }]),
    }),
    removeContactFromList: builder.mutation<void, { listId: string; contactId: string }>({
      query: ({ listId, contactId }) => ({
        url: `/v1/contact-lists/${listId}/contacts/${contactId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { listId }) => (error ? [] : [{ type: "ContactListContacts", id: listId }]),
    }),
    getContactCalls: builder.query<
      PagedResponse<Call>,
      { contactId: string; page?: number; size?: number }
    >({
      query: ({ contactId, page = 0, size = 20 }) => ({
        url: `/v1/contacts/${contactId}/calls`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["Calls"],
    }),
    getContactMessages: builder.query<
      PagedResponse<Message>,
      { contactId: string; page?: number; size?: number }
    >({
      query: ({ contactId, page = 0, size = 20 }) => ({
        url: `/v1/contacts/${contactId}/messages`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["Messages"],
    }),
    getContactActivitySummary: builder.query<ContactActivitySummary, string>({
      query: (contactId) => ({
        url: `/v1/contacts/${contactId}/activity-summary`,
        method: "get",
      }),
      providesTags: ["Calls", "Messages"],
    }),
    getConversations: builder.query<PagedResponse<Conversation>, { page?: number; size?: number; archived?: boolean }>({
      query: ({ page = 0, size = 20, archived } = {}) => ({
        url: "/v1/conversations",
        method: "get",
        params: { page, size, archived },
      }),
      providesTags: ["Conversations"],
    }),
    getConversation: builder.query<Conversation, string>({
      query: (conversationId) => ({
        url: `/v1/conversations/${conversationId}`,
        method: "get",
      }),
      providesTags: ["Conversations"],
    }),
    getMessages: builder.query<Message[], string>({
      query: (conversationId) => ({
        url: `/v1/conversations/${conversationId}/messages`,
        method: "get",
      }),
      providesTags: ["Messages"],
    }),
    sendMessage: builder.mutation<
      Message,
      {
        fromPhoneNumber: { id: string };
        toNumbers: string[];
        body?: string;
        mediaUrls?: string[];
        /** Set only after the sender accepted that a recipient is outside their contact window. */
        acknowledgeQuietHours?: boolean;
        acknowledgeContactWarning?: boolean;
        suppressContactWarning?: boolean;
        dncCheck?: DncCheckMode;
      }
    >({
      query: (data) => ({
        url: "/v1/conversations/send",
        method: "post",
        data: { ...data, platform: DEVICE_PLATFORM },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "Messages", "CommunicationItems"]),
    }),
    getTextsReport: builder.query<TextsReport, { from: string; to: string }>({
      query: (params) => ({
        url: "/v1/conversations/texts-report",
        method: "get",
        params,
      }),
    }),
    markConversationRead: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/v1/conversations/${conversationId}/read`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    archiveConversation: builder.mutation<Conversation, string>({
      query: (id) => ({
        url: `/v1/conversations/${id}/archive`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    restoreConversation: builder.mutation<Conversation, string>({
      query: (id) => ({
        url: `/v1/conversations/${id}/restore`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    deleteConversationPermanently: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/conversations/${id}/permanent`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "Messages", "CommunicationItems"]),
    }),
    flagConversation: builder.mutation<Conversation, { id: string; flagged: boolean }>({
      query: ({ id, flagged }) => ({
        url: `/v1/conversations/${id}/flag`,
        method: "patch",
        data: { flagged },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    getUnreadTotal: builder.query<{ total: number }, void>({
      query: () => ({
        url: "/v1/conversations/unread-total",
        method: "get",
      }),
      providesTags: ["Conversations"],
    }),
    addParticipant: builder.mutation<Conversation, { conversationId: string; phoneNumber: string }>({
      query: ({ conversationId, phoneNumber }) => ({
        url: `/v1/conversations/${conversationId}/participants`,
        method: "post",
        data: { phoneNumber },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations"]),
    }),
    getCommunicationItems: builder.infiniteQuery<CommunicationItemPage, CommunicationItemFilter, CommunicationItemPageParam>({
      infiniteQueryOptions: {
        initialPageParam: {},
        getNextPageParam: (lastPage) => (lastPage.olderCursor ? { before: lastPage.olderCursor } : undefined),
        getPreviousPageParam: (firstPage) => (firstPage.newerCursor ? { after: firstPage.newerCursor } : undefined),
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/v1/communication-items",
        method: "get",
        params: {
          contactId: queryArg.contactId,
          conversationId: queryArg.conversationId,
          kind: queryArg.kind?.join(","),
          direction: queryArg.direction,
          needsAttention: queryArg.needsAttention,
          hasNote: queryArg.hasNote,
          archived: queryArg.archived,
          flagged: queryArg.flagged,
          grouped: queryArg.grouped,
          includeNotes: queryArg.includeNotes,
          q: queryArg.q,
          from: queryArg.from,
          to: queryArg.to,
          size: queryArg.size,
          before: pageParam.before,
          after: pageParam.after,
          around: pageParam.around,
        },
      }),
      providesTags: ["CommunicationItems"],
    }),
    getAttentionCount: builder.query<AttentionCount, void>({
      query: () => ({
        url: "/v1/communication-items/attention-count",
        method: "get",
      }),
      providesTags: ["CommunicationItems", "Calls"],
    }),
    markCommunicationItemsSeen: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/v1/communication-items/seen",
        method: "post",
        data: ids.map((id) => ({ id })),
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    archiveThread: builder.mutation<void, string>({
      query: (contactId) => ({
        url: "/v1/communication-items/archive",
        method: "post",
        params: { contactId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    restoreThread: builder.mutation<void, string>({
      query: (contactId) => ({
        url: "/v1/communication-items/restore",
        method: "post",
        params: { contactId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Conversations", "CommunicationItems"]),
    }),
    rebuildCommunicationItems: builder.mutation<{ repaired: number; resynced: number }, void>({
      query: () => ({
        url: "/v1/platform-admin/communication-items/rebuild",
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CommunicationItems", "Contacts", "Conversations"]),
    }),

    // Call Dispositions
    getCallDispositions: builder.query<CallDisposition[], { all?: boolean }>({
      query: ({ all } = {}) => ({
        url: "/v1/call-dispositions",
        method: "get",
        params: all ? { all: true } : undefined,
      }),
      providesTags: ["CallDispositions"],
    }),
    createCallDisposition: builder.mutation<CallDisposition, Partial<CallDisposition>>({
      query: (data) => ({
        url: "/v1/call-dispositions",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallDispositions"]),
    }),
    updateCallDisposition: builder.mutation<CallDisposition, { id: string; data: Partial<CallDisposition> }>({
      query: ({ id, data }) => ({
        url: `/v1/call-dispositions/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallDispositions"]),
    }),
    deleteCallDisposition: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/call-dispositions/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallDispositions"]),
    }),
    getCallScripts: builder.query<CallScript[], { all?: boolean }>({
      query: ({ all } = {}) => ({
        url: "/v1/call-scripts",
        method: "get",
        params: all ? { all: true } : undefined,
      }),
      providesTags: ["CallScripts"],
    }),
    createCallScript: builder.mutation<CallScript, CallScriptRequest>({
      query: (data) => ({
        url: "/v1/call-scripts",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallScripts"]),
    }),
    updateCallScript: builder.mutation<CallScript, { id: string; data: CallScriptRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/call-scripts/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallScripts"]),
    }),
    deleteCallScript: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/call-scripts/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallScripts"]),
    }),

    getTeams: builder.query<Team[], void>({
      query: () => ({
        url: "/v1/teams",
        method: "get",
      }),
      providesTags: ["Teams"],
    }),
    createTeam: builder.mutation<Team, TeamRequest>({
      query: (data) => ({
        url: "/v1/teams",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Teams"]),
    }),
    updateTeam: builder.mutation<Team, { id: string; data: TeamRequest }>({
      query: ({ id, data }) => ({
        url: `/v1/teams/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Teams"]),
    }),
    deleteTeam: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/teams/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Teams"]),
    }),

    // Tags
    getTags: builder.query<Tag[], { all?: boolean }>({
      query: ({ all } = {}) => ({
        url: "/v1/tags",
        method: "get",
        params: all ? { all: true } : undefined,
      }),
      providesTags: ["Tags"],
    }),
    createTag: builder.mutation<Tag, Partial<Tag>>({
      query: (data) => ({
        url: "/v1/tags",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tags"]),
    }),
    updateTag: builder.mutation<Tag, { id: string; data: Partial<Tag> }>({
      query: ({ id, data }) => ({
        url: `/v1/tags/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tags"]),
    }),
    deleteTag: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/tags/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tags"]),
    }),
    bulkAddTag: builder.mutation<{ added: number }, { tagId: string; contactIds: string[] }>({
      query: ({ tagId, contactIds }) => ({
        url: `/v1/tags/${tagId}/contacts`,
        method: "post",
        data: { contactIds },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts"]),
    }),
    bulkRemoveTag: builder.mutation<{ removed: number }, { tagId: string; contactIds: string[] }>({
      query: ({ tagId, contactIds }) => ({
        url: `/v1/tags/${tagId}/contacts`,
        method: "delete",
        data: { contactIds },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts"]),
    }),

    // Tasks
    getTasks: builder.query<
      PagedResponse<Task>,
      { page?: number; size?: number; q?: string; status?: TaskStatus; priority?: TaskPriority; assignedMembershipId?: number; contactId?: string; dealId?: string; propertyId?: string }
    >({
      query: ({ page = 0, size = 20, q, status, priority, assignedMembershipId, contactId, dealId, propertyId }) => ({
        url: "/v1/tasks",
        method: "get",
        params: {
          page, size,
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
          ...(priority ? { priority } : {}),
          ...(assignedMembershipId ? { assignedMembershipId } : {}),
          ...(contactId ? { contactId } : {}),
          ...(dealId ? { dealId } : {}),
          ...(propertyId ? { propertyId } : {}),
        },
      }),
      providesTags: ["Tasks"],
    }),
    getTask: builder.query<Task, string>({
      query: (id) => ({
        url: `/v1/tasks/${id}`,
        method: "get",
      }),
      providesTags: ["Tasks"],
    }),
    createTask: builder.mutation<Task, Partial<Task>>({
      query: (data) => ({
        url: "/v1/tasks",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tasks"]),
    }),
    updateTask: builder.mutation<Task, { id: string; data: Partial<Task> }>({
      query: ({ id, data }) => ({
        url: `/v1/tasks/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tasks"]),
    }),
    updateTaskStatus: builder.mutation<Task, { id: string; status: TaskStatus }>({
      query: ({ id, status }) => ({
        url: `/v1/tasks/${id}/status`,
        method: "patch",
        data: { status },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tasks"]),
    }),
    deleteTask: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/tasks/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Tasks"]),
    }),

    // Workflows
    getWorkflows: builder.query<
      PagedResponse<Workflow>,
      { page?: number; size?: number; q?: string; status?: WorkflowStatus }
    >({
      query: (params) => ({
        url: "/v1/workflows",
        method: "get",
        params: {
          page: params.page ?? 0,
          size: params.size ?? 20,
          q: params.q,
          status: params.status,
        },
      }),
      providesTags: ["Workflows"],
    }),
    getWorkflow: builder.query<Workflow, string>({
      query: (id) => ({
        url: `/v1/workflows/${id}`,
        method: "get",
      }),
      providesTags: ["Workflows"],
    }),
    createWorkflow: builder.mutation<Workflow, {
      name: string;
      description?: string;
      triggerType: WorkflowTriggerType;
      triggerConfig?: string;
      status?: WorkflowStatus;
      actions?: { id?: string; actionType: WorkflowActionType; config?: string; delayMinutes?: number }[];
    }>({
      query: (data) => ({
        url: "/v1/workflows",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Workflows"]),
    }),
    updateWorkflow: builder.mutation<Workflow, {
      id: string;
      data: {
        name: string;
        description?: string;
        triggerType: WorkflowTriggerType;
        triggerConfig?: string;
        status?: WorkflowStatus;
        actions?: { id?: string; actionType: WorkflowActionType; config?: string; delayMinutes?: number }[];
      };
    }>({
      query: ({ id, data }) => ({
        url: `/v1/workflows/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Workflows"]),
    }),
    updateWorkflowStatus: builder.mutation<Workflow, { id: string; status: WorkflowStatus }>({
      query: ({ id, status }) => ({
        url: `/v1/workflows/${id}/status`,
        method: "patch",
        data: { status },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Workflows"]),
    }),
    deleteWorkflow: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/workflows/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Workflows"]),
    }),
    getWebhookCredentials: builder.query<WorkflowWebhookCredential[], string>({
      query: (workflowId) => ({
        url: `/v1/workflows/${workflowId}/webhook-credentials`,
        method: "get",
      }),
      providesTags: (_, _err, workflowId) => [{ type: "WebhookCredentials", id: workflowId }],
    }),
    createWebhookCredential: builder.mutation<
      WorkflowWebhookCredential,
      { workflowId: string; name: string }
    >({
      query: ({ workflowId, name }) => ({
        url: `/v1/workflows/${workflowId}/webhook-credentials`,
        method: "post",
        data: { name },
      }),
      invalidatesTags: (_, error, { workflowId }) =>
        error ? [] : [{ type: "WebhookCredentials", id: workflowId }],
    }),
    updateWebhookCredential: builder.mutation<
      WorkflowWebhookCredential,
      { workflowId: string; credentialId: string; name?: string; enabled?: boolean }
    >({
      query: ({ workflowId, credentialId, ...data }) => ({
        url: `/v1/workflows/${workflowId}/webhook-credentials/${credentialId}`,
        method: "patch",
        data,
      }),
      invalidatesTags: (_, error, { workflowId }) =>
        error ? [] : [{ type: "WebhookCredentials", id: workflowId }],
    }),
    deleteWebhookCredential: builder.mutation<
      void,
      { workflowId: string; credentialId: string }
    >({
      query: ({ workflowId, credentialId }) => ({
        url: `/v1/workflows/${workflowId}/webhook-credentials/${credentialId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error, { workflowId }) =>
        error ? [] : [{ type: "WebhookCredentials", id: workflowId }],
    }),
    getWorkflowExecutions: builder.query<
      PagedResponse<WorkflowExecution>,
      { workflowId: string; page?: number; size?: number }
    >({
      query: ({ workflowId, page, size }) => ({
        url: `/v1/workflows/${workflowId}/executions`,
        method: "get",
        params: { page: page ?? 0, size: size ?? 20 },
      }),
      providesTags: ["WorkflowExecutions"],
    }),
    cancelWorkflowExecution: builder.mutation<void, string>({
      query: (executionId) => ({
        url: `/v1/workflows/executions/${executionId}/cancel`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["WorkflowExecutions"]),
    }),

    updateCall: builder.mutation<Call, { id: string; disposition?: { id: string }; notes?: string }>({
      query: ({ id, ...data }) => ({
        url: `/v1/calls/${id}`,
        method: "patch",
        data,
      }),
      async onQueryStarted(arg, lifecycle) {
        const fullDisposition = arg.disposition?.id
          ? findCachedDisposition(restApi, lifecycle, arg.disposition.id)
          : undefined;
        const patches = patchCallInAllCaches(restApi, lifecycle, arg.id, (call) => {
          if (fullDisposition) {
            call.disposition = fullDisposition;
          }
          if (arg.notes !== undefined) {
            call.notes = arg.notes;
          }
        });
        await undoOnError(lifecycle.queryFulfilled, patches);
      },
      invalidatesTags: (_, error, arg) => {
        if (error) {
          return [];
        }
        if (arg.disposition) {
          return ["Calls", "CommunicationItems", "Contacts", "DncEntries", "Deals", "Tasks", "DialerQueue"];
        }
        return ["Calls", "CommunicationItems"];
      },
    }),

    // Message Notes
    createMessageNote: builder.mutation<MessageNote, { messageId: string; body: string }>({
      query: ({ messageId, body }) => ({
        url: `/v1/conversations/messages/${messageId}/notes`,
        method: "post",
        data: { body },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Messages", "CommunicationItems"]),
    }),
    updateMessageNote: builder.mutation<MessageNote, { noteId: string; body: string }>({
      query: ({ noteId, body }) => ({
        url: `/v1/conversations/messages/notes/${noteId}`,
        method: "put",
        data: { body },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Messages", "CommunicationItems"]),
    }),
    deleteMessageNote: builder.mutation<void, string>({
      query: (noteId) => ({
        url: `/v1/conversations/messages/notes/${noteId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Messages", "CommunicationItems"]),
    }),

    // Review of inbound messages flagged as a possible opt-out
    confirmPossibleOptOut: builder.mutation<Message, string>({
      query: (messageId) => ({
        url: `/v1/conversations/messages/${messageId}/opt-out/confirm`,
        method: "post",
      }),
      // Confirming changes consent and DNC, so contact views must refetch too.
      invalidatesTags: (_, error) => (error ? [] : ["Messages", "Contacts"]),
    }),
    dismissPossibleOptOut: builder.mutation<Message, string>({
      query: (messageId) => ({
        url: `/v1/conversations/messages/${messageId}/opt-out/dismiss`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Messages"]),
    }),

    // Consent
    getConsentHistory: builder.query<
      PagedResponse<ConsentEvent>,
      { contactId: string; page?: number; size?: number }
    >({
      query: ({ contactId, page = 0, size = 20 }) => ({
        url: `/v1/contacts/${contactId}/consent`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["ConsentEvents"],
    }),
    grantConsent: builder.mutation<
      ConsentEvent,
      {
        contactId: string;
        consentType: string;
        source: string;
        consentBasis?: string;
        phoneNumberE164?: string;
        notes?: string;
        evidenceUrl?: string;
      }
    >({
      query: ({ contactId, ...data }) => ({
        url: `/v1/contacts/${contactId}/consent/grant`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ["ConsentEvents", "Contacts"],
    }),
    grantConsentByNumber: builder.mutation<
      { contactsGranted: number },
      {
        phoneNumberE164: string;
        consentType?: string;
        source?: string;
        notes?: string;
      }
    >({
      query: (data) => ({
        url: "/v1/contacts/consent/grant-by-number",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ["ConsentEvents", "Contacts"],
    }),
    revokeConsent: builder.mutation<
      ConsentEvent,
      {
        contactId: string;
        consentType: string;
        source: string;
        phoneNumberE164?: string;
        notes?: string;
      }
    >({
      query: ({ contactId, ...data }) => ({
        url: `/v1/contacts/${contactId}/consent/revoke`,
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ["ConsentEvents", "Contacts"],
    }),

    // DNC
    getDncEntries: builder.query<
      PagedResponse<DncEntry>,
      { page?: number; size?: number; type?: DncType; phoneNumbers?: string[] }
    >({
      query: ({ page = 0, size = 50, type, phoneNumbers }) => ({
        url: "/v1/dnc",
        method: "get",
        params: {
          page,
          size,
          sort: "createdAt,desc",
          ...(type ? { type } : {}),
          ...(phoneNumbers?.length ? { phoneNumbers: phoneNumbers.join(",") } : {}),
        },
      }),
      providesTags: ["DncEntries"],
    }),
    addDncEntry: builder.mutation<DncEntry, { phoneNumberE164: string; type?: DncType }>({
      query: (data) => ({
        url: "/v1/dnc",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DncEntries", "Contacts"]),
    }),
    removeDncEntry: builder.mutation<void, number>({
      query: (id) => ({
        url: `/v1/dnc/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DncEntries", "Contacts"]),
    }),
    getEmailSuppressions: builder.query<
      PagedResponse<EmailSuppression>,
      { page?: number; size?: number; search?: string }
    >({
      query: ({ page = 0, size = 50, search }) => ({
        url: "/v1/email-suppressions",
        method: "get",
        params: { page, size, ...(search ? { search } : {}) },
      }),
      providesTags: ["EmailSuppressions"],
    }),
    addEmailSuppression: builder.mutation<EmailSuppression, { emailAddress: string; notes?: string }>({
      query: (data) => ({
        url: "/v1/email-suppressions",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["EmailSuppressions", "Contacts"]),
    }),
    removeEmailSuppression: builder.mutation<void, string>({
      query: (emailAddress) => ({
        url: "/v1/email-suppressions",
        method: "delete",
        params: { emailAddress },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["EmailSuppressions", "Contacts"]),
    }),
    getUnsubscribeTarget: builder.query<UnsubscribeTarget, string>({
      query: (token) => ({
        url: "/v1/public/unsubscribe",
        method: "get",
        params: { token },
      }),
    }),
    unsubscribeEmail: builder.mutation<UnsubscribeTarget, string>({
      query: (token) => ({
        url: "/v1/public/unsubscribe",
        method: "post",
        params: { token },
      }),
    }),
    removeFromDncList: builder.mutation<void, { phoneNumber: string; type: DncType }>({
      query: (params) => ({
        url: "/v1/dnc",
        method: "delete",
        params,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DncEntries", "Contacts"]),
    }),
    checkDnc: builder.mutation<
      { phoneNumberE164: string; doNotCall: boolean; doNotText: boolean; inboundBlocked: boolean },
      string
    >({
      query: (phoneNumberE164) => ({
        url: "/v1/dnc/check",
        method: "post",
        data: { phoneNumberE164 },
      }),
    }),
    getDncLookupPrices: builder.query<DncLookupPrices, void>({
      query: () => ({
        url: "/v1/dnc/lookup/prices",
        method: "get",
      }),
    }),
    lookUpDncNumbers: builder.mutation<DncLookupResponse, { phoneNumbers: string[]; mode: DncCheckMode }>({
      query: (data) => ({
        url: "/v1/dnc/lookup",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Contacts", "Billing"]),
    }),

    // Deals
    getDeals: builder.query<
      PagedResponse<Deal>,
      {
        page?: number;
        size?: number;
        q?: string;
        stageId?: string;
        category?: DealStageCategory;
        includeLeads?: boolean;
        assignedMembershipId?: number;
        contactId?: string;
        sort?: string;
      }
    >({
      query: ({ page = 0, size = 20, q, stageId, category, includeLeads, assignedMembershipId, contactId, sort = "createdAt,desc" }) => ({
        url: "/v1/deals",
        method: "get",
        params: {
          page,
          size,
          sort,
          ...(q ? { q } : {}),
          ...(stageId ? { stageId } : {}),
          ...(category ? { category } : {}),
          ...(includeLeads ? { includeLeads } : {}),
          ...(assignedMembershipId ? { assignedMembershipId } : {}),
          ...(contactId ? { contactId } : {}),
        },
      }),
      providesTags: ["Deals"],
    }),
    getDeal: builder.query<Deal, string>({
      query: (id) => ({
        url: `/v1/deals/${id}`,
        method: "get",
      }),
      providesTags: ["Deals"],
    }),
    createDeal: builder.mutation<Deal, Partial<Deal>>({
      query: (data) => ({
        url: "/v1/deals",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Deals", "CommunicationItems", "Contacts", "LeadSummary"]),
    }),
    updateDeal: builder.mutation<Deal, { id: string; data: Partial<Deal> }>({
      query: ({ id, data }) => ({
        url: `/v1/deals/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Deals", "DealActivities", "Contacts", "LeadSummary"]),
    }),
    updateDealStage: builder.mutation<Deal, { id: string; stageId: string }>({
      query: ({ id, stageId }) => ({
        url: `/v1/deals/${id}/stage`,
        method: "put",
        data: { stageId },
      }),
      async onQueryStarted(arg, lifecycle) {
        const stage = findCachedDealStage(restApi, lifecycle, arg.stageId);
        if (!stage) {
          return;
        }
        const patches = [
          ...patchDealInAllCaches(restApi, lifecycle, arg.id, (deal) => {
            deal.stage = stage;
          }),
          ...patchContactActiveDealInAllCaches(restApi, lifecycle, arg.id, (activeDeal) => {
            activeDeal.stage = stage;
          }),
        ];
        await undoOnError(lifecycle.queryFulfilled, patches);
      },
      invalidatesTags: (_, error) => (error ? [] : ["Deals", "DealActivities", "Contacts", "LeadSummary"]),
    }),
    deleteDeal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/deals/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Deals"]),
    }),
    getDealActivities: builder.query<
      PagedResponse<DealActivity>,
      { dealId: string; page?: number; size?: number }
    >({
      query: ({ dealId, page = 0, size = 20 }) => ({
        url: `/v1/deals/${dealId}/activities`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["DealActivities"],
    }),
    getPipelineSummary: builder.query<PipelineSummary, void>({
      query: () => ({
        url: "/v1/deals/pipeline-summary",
        method: "get",
      }),
      providesTags: ["Deals"],
    }),

    // Deal Stages
    getDealStages: builder.query<DealStage[], { all?: boolean }>({
      query: ({ all } = {}) => ({
        url: "/v1/deal-stages",
        method: "get",
        params: all ? { all: true } : undefined,
      }),
      providesTags: ["DealStages"],
    }),
    createDealStage: builder.mutation<DealStage, Partial<DealStage>>({
      query: (data) => ({
        url: "/v1/deal-stages",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DealStages", "Deals"]),
    }),
    updateDealStageConfig: builder.mutation<DealStage, { id: string; data: Partial<DealStage> }>({
      query: ({ id, data }) => ({
        url: `/v1/deal-stages/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DealStages", "Deals", "Contacts", "LeadSummary"]),
    }),
    reorderDealStages: builder.mutation<DealStage[], Pick<DealStage, "id" | "category">[]>({
      query: (data) => ({
        url: "/v1/deal-stages/order",
        method: "put",
        data,
      }),
      async onQueryStarted(order, lifecycle) {
        const patches = reorderDealStagesInAllCaches(restApi, lifecycle, order);
        await undoOnError(lifecycle.queryFulfilled, patches);
      },
      invalidatesTags: (_, error) => (error ? [] : ["DealStages", "Deals", "Contacts", "LeadSummary"]),
    }),
    makeDefaultDealStage: builder.mutation<DealStage[], string>({
      query: (id) => ({
        url: `/v1/deal-stages/${id}/default`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DealStages"]),
    }),
    getDealStageUsage: builder.query<DealStageUsage, string>({
      query: (id) => ({
        url: `/v1/deal-stages/${id}/usage`,
        method: "get",
      }),
      providesTags: ["DealStages"],
    }),
    deleteDealStage: builder.mutation<void, { id: string; moveDealsTo?: string }>({
      query: ({ id, moveDealsTo }) => ({
        url: `/v1/deal-stages/${id}`,
        method: "delete",
        params: moveDealsTo ? { moveDealsTo } : undefined,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DealStages", "Deals", "Contacts", "LeadSummary", "CallDispositions"]),
    }),
    getDealStageSettings: builder.query<DealStageSettings, void>({
      query: () => ({
        url: "/v1/deal-stages/settings",
        method: "get",
      }),
      providesTags: ["DealStages"],
    }),
    updateLeadAutoPromote: builder.mutation<void, boolean>({
      query: (enabled) => ({
        url: "/v1/deal-stages/settings/lead-auto-promote",
        method: "put",
        data: { value: enabled ? "ENABLED" : "DISABLED" },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DealStages"]),
    }),

    // Properties
    getProperties: builder.query<
      PagedResponse<Property>,
      { page?: number; size?: number; q?: string; sort?: string }
    >({
      query: ({ page = 0, size = 20, q, sort = "createdAt,desc" }) => ({
        url: "/v1/properties",
        method: "get",
        params: { page, size, sort, ...(q ? { q } : {}) },
      }),
      providesTags: ["Properties"],
    }),
    getProperty: builder.query<Property, string>({
      query: (id) => ({
        url: `/v1/properties/${id}`,
        method: "get",
      }),
      providesTags: ["Properties"],
    }),
    getPropertyContacts: builder.query<Contact[], string>({
      query: (id) => ({
        url: `/v1/properties/${id}/contacts`,
        method: "get",
      }),
      providesTags: ["Contacts"],
    }),
    createProperty: builder.mutation<Property, Partial<Property>>({
      query: (data) => ({
        url: "/v1/properties",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Properties"]),
    }),
    updateProperty: builder.mutation<Property, { id: string; data: Partial<Property> }>({
      query: ({ id, data }) => ({
        url: `/v1/properties/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Properties"]),
    }),
    deleteProperty: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/properties/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Properties"]),
    }),
    linkPropertyContact: builder.mutation<void, { propertyId: string; contactId: string }>({
      query: ({ propertyId, contactId }) => ({
        url: `/v1/properties/${propertyId}/contacts/${contactId}`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Properties", "Contacts"]),
    }),
    unlinkPropertyContact: builder.mutation<void, { propertyId: string; contactId: string }>({
      query: ({ propertyId, contactId }) => ({
        url: `/v1/properties/${propertyId}/contacts/${contactId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Properties", "Contacts"]),
    }),
    uploadFiles: builder.mutation<FileResponse[], { files: File[]; type?: string }>({
      query: ({ files, type = "uncategorized" }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("file[]", f));
        formData.append("type", type);

        return {
          url: "/v1/files",
          method: "POST",
          data: formData,
          timeout: 600000,
        };
      },
    }),
    createImport: builder.mutation<ImportSetup, { fileId: string; entityType: ImportEntityType; contactType?: string | null }>({
      query: (body) => ({
        url: "/v1/imports",
        method: "POST",
        data: body,
        timeout: 300000,
      }),
    }),
    selectImportSheet: builder.mutation<ImportSetup, { importId: string; sheetName: string }>({
      query: ({ importId, sheetName }) => ({
        url: `/v1/imports/${importId}/sheet`,
        method: "POST",
        data: { sheetName },
        timeout: 300000,
      }),
    }),
    getImportValues: builder.mutation<ImportValueField[], { importId: string; mapping: ImportMappingSpec }>({
      query: ({ importId, mapping }) => ({
        url: `/v1/imports/${importId}/values`,
        method: "POST",
        data: mapping,
        timeout: 300000,
      }),
    }),
    previewImport: builder.mutation<ImportPreviewRow[], { importId: string; mapping: ImportMappingSpec }>({
      query: ({ importId, mapping }) => ({
        url: `/v1/imports/${importId}/preview`,
        method: "POST",
        data: mapping,
        timeout: 300000,
      }),
    }),
    runImport: builder.mutation<ImportRunSummary, { importId: string; mapping: ImportMappingSpec; options: ImportRunOptions }>({
      query: ({ importId, mapping, options }) => ({
        url: `/v1/imports/${importId}/run`,
        method: "POST",
        data: { mapping, options },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Imports"]),
    }),
    getImportRuns: builder.query<PagedResponse<ImportRunSummary>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 20 }) => ({
        url: "/v1/imports/runs",
        method: "GET",
        params: { page, size },
      }),
      providesTags: ["Imports"],
    }),
    getImportRun: builder.query<ImportRunSummary, string>({
      query: (runId) => ({
        url: `/v1/imports/runs/${runId}`,
        method: "GET",
      }),
      providesTags: ["Imports"],
    }),
    getImportRunErrors: builder.query<ImportRowError[], string>({
      query: (runId) => ({
        url: `/v1/imports/runs/${runId}/errors`,
        method: "GET",
        timeout: 60000,
      }),
    }),
    undoImportRun: builder.mutation<ImportRunSummary, string>({
      query: (runId) => ({
        url: `/v1/imports/runs/${runId}/undo`,
        method: "POST",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Imports"]),
    }),
    createExport: builder.mutation<ExportSummary, ExportRequest>({
      query: (data) => ({
        url: "/v1/exports",
        method: "POST",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Exports"]),
    }),
    getExports: builder.query<PagedResponse<ExportSummary>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 20 }) => ({
        url: "/v1/exports",
        method: "GET",
        params: { page, size },
      }),
      providesTags: ["Exports"],
    }),
    getExport: builder.query<ExportSummary, string>({
      query: (exportId) => ({
        url: `/v1/exports/${exportId}`,
        method: "GET",
      }),
      providesTags: ["Exports"],
    }),
    getExportDownload: builder.query<ExportDownload, string>({
      query: (exportId) => ({
        url: `/v1/exports/${exportId}/download`,
        method: "GET",
      }),
    }),
    searchExternalProperties: builder.mutation<
      PagedResponse<ExternalPropertyResult>,
      PropertySearchCriteria
    >({
      query: (data) => ({
        url: "/v1/properties/search-external",
        method: "POST",
        data,
      }),
    }),
    importExternalProperties: builder.mutation<
      Property[],
      { externalIds: string[]; searchCriteria: PropertySearchCriteria }
    >({
      query: (data) => ({
        url: "/v1/properties/import-external",
        method: "POST",
        data,
      }),
      invalidatesTags: ["Properties", "PropertyDataUsage"],
    }),
    getPropertyDataUsage: builder.query<PropertyDataUsage, void>({
      query: () => ({
        url: "/v1/properties/data-usage",
        method: "get",
      }),
      providesTags: ["PropertyDataUsage"],
    }),
    previewListings: builder.mutation<PropertyPreviewResult, { criteria: PropertySearchCriteria; count: number }>({
      query: ({ criteria, count }) => ({
        url: "/v1/properties/listings/preview",
        method: "POST",
        data: criteria,
        params: { count },
      }),
    }),
    checkDuplicateSearch: builder.mutation<DuplicateSearchWarning, PropertySearchCriteria>({
      query: (criteria) => ({
        url: "/v1/properties/listings/check-duplicate",
        method: "POST",
        data: criteria,
      }),
    }),
    startListingsBuild: builder.mutation<PropertyBuildStatus, { criteria: PropertySearchCriteria; count: number }>({
      query: ({ criteria, count }) => ({
        url: "/v1/properties/listings/build",
        method: "POST",
        data: criteria,
        params: { count },
      }),
      invalidatesTags: ["PropertyDataSearches"],
    }),
    getListingsBuildStatus: builder.query<PropertyBuildStatus, number>({
      query: (id) => ({
        url: `/v1/properties/listings/build/${id}`,
        method: "get",
      }),
    }),
    importNextListings: builder.mutation<PropertyBuildStatus, { id: number; batch?: number }>({
      query: ({ id, batch = 10 }) => ({
        url: `/v1/properties/listings/build/${id}/import-next`,
        method: "POST",
        params: { batch },
      }),
      invalidatesTags: ["Properties", "PropertyDataUsage", "PropertyDataSearches"],
    }),
    getListingsSearches: builder.query<PropertyBuildStatus[], void>({
      query: () => ({
        url: "/v1/properties/listings/searches",
        method: "get",
      }),
      providesTags: ["PropertyDataSearches"],
    }),

    // A2P Registration
    getA2pRegistration: builder.query<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration", method: "get" }),
      providesTags: ["A2pRegistration"],
    }),
    saveA2pDraft: builder.mutation<A2pRegistration, Partial<A2pRegistration>>({
      query: (data) => ({ url: "/v1/a2p-registration/draft", method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    submitA2pCustomerProfile: builder.mutation<A2pRegistration, Partial<A2pRegistration>>({
      query: (data) => ({ url: "/v1/a2p-registration/customer-profile", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    submitA2pBrand: builder.mutation<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration/brand", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    generateA2pContent: builder.mutation<A2pRegistration, { force?: boolean } | void>({
      query: (args) => ({
        url: "/v1/a2p-registration/generate-content",
        method: "post",
        params: args && args.force ? { force: true } : undefined,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    createA2pMessagingService: builder.mutation<A2pRegistration, { phoneNumberSids: string[] }>({
      query: (data) => ({ url: "/v1/a2p-registration/messaging-service", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    submitA2pCampaign: builder.mutation<A2pRegistration, Partial<A2pRegistration>>({
      query: (data) => ({ url: "/v1/a2p-registration/campaign", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    refreshA2pStatus: builder.mutation<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration/refresh-status", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    cancelA2pRegistration: builder.mutation<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration/cancel", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    resendA2pOtp: builder.mutation<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration/resend-otp", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration"]),
    }),
    purchaseAssistedA2p: builder.mutation<A2pRegistration, void>({
      query: () => ({ url: "/v1/a2p-registration/assisted-purchase", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pRegistration", "Billing"]),
    }),
    getA2pCampaigns: builder.query<A2pCampaign[], void>({
      query: () => ({ url: "/v1/a2p-registration/campaigns", method: "get" }),
      providesTags: ["A2pCampaigns"],
    }),
    createA2pCampaign: builder.mutation<A2pCampaign, Partial<A2pCampaign>>({
      query: (data) => ({ url: "/v1/a2p-registration/campaigns", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pCampaigns"]),
    }),
    updateA2pCampaign: builder.mutation<A2pCampaign, { campaignId: string; data: Partial<A2pCampaign> }>({
      query: ({ campaignId, data }) => ({
        url: `/v1/a2p-registration/campaigns/${campaignId}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pCampaigns"]),
    }),
    submitA2pCampaignById: builder.mutation<A2pCampaign, string>({
      query: (campaignId) => ({
        url: `/v1/a2p-registration/campaigns/${campaignId}/submit`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pCampaigns", "A2pRegistration"]),
    }),
    cancelA2pCampaignById: builder.mutation<A2pCampaign, string>({
      query: (campaignId) => ({
        url: `/v1/a2p-registration/campaigns/${campaignId}/cancel`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pCampaigns", "A2pRegistration", "PhoneNumbers"]),
    }),
    assignA2pCampaignNumbers: builder.mutation<A2pCampaign, { campaignId: string; phoneNumberIds: string[] }>({
      query: ({ campaignId, phoneNumberIds }) => ({
        url: `/v1/a2p-registration/campaigns/${campaignId}/numbers`,
        method: "put",
        data: { phoneNumberIds },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pCampaigns", "PhoneNumbers"]),
    }),

    // Notifications
    getNotifications: builder.query<
      PagedResponse<Notification>,
      { page?: number; size?: number }
    >({
      query: ({ page = 0, size = 20 }) => ({
        url: "/v1/notifications",
        method: "get",
        params: { page, size },
      }),
      providesTags: ["Notifications"],
    }),
    getUnreadNotificationCount: builder.query<number, void>({
      query: () => ({ url: "/v1/notifications/unread-count", method: "get" }),
      providesTags: ["Notifications"],
    }),
    markNotificationAsRead: builder.mutation<Notification, string>({
      query: (id) => ({ url: `/v1/notifications/${id}/read`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Notifications"]),
    }),
    markNotificationsSeen: builder.mutation<number, string[]>({
      query: (ids) => ({
        url: "/v1/notifications/seen",
        method: "post",
        data: ids.map((id) => ({ id })),
      }),
      async onQueryStarted(ids, lifecycle) {
        const patches = markNotificationsSeenInAllCaches(restApi, lifecycle, ids);
        await undoOnError(lifecycle.queryFulfilled, patches);
      },
      invalidatesTags: (_, error) => (error ? [] : ["Notifications"]),
    }),
    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({ url: "/v1/notifications/read-all", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Notifications"]),
    }),
    getNotificationPreferences: builder.query<NotificationPreference[], void>({
      query: () => ({ url: "/v1/notifications/preferences", method: "get" }),
      providesTags: ["NotificationPreferences"],
    }),
    updateNotificationPreferences: builder.mutation<NotificationPreference[], NotificationPreference[]>({
      query: (data) => ({ url: "/v1/notifications/preferences", method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["NotificationPreferences", "Checklist"]),
    }),

    // AI Agents
    getAiAgents: builder.query<AiAgent[], void>({
      query: () => ({ url: "/v1/ai-agents", method: "get" }),
      providesTags: ["AiAgents"],
    }),
    getAiVoices: builder.query<VoiceOption[], void>({
      query: () => ({ url: "/v1/ai-agents/voices", method: "get" }),
    }),
    getAgentVoices: builder.query<VoiceOption[], string>({
      query: (agentId) => ({ url: `/v1/ai-agents/${agentId}/voices`, method: "get" }),
    }),
    getAiOutcomes: builder.query<string[], void>({
      query: () => ({ url: "/v1/ai-agents/outcomes", method: "get" }),
    }),
    getAiAgent: builder.query<AiAgent, string>({
      query: (id) => ({ url: `/v1/ai-agents/${id}`, method: "get" }),
      providesTags: ["AiAgents"],
    }),
    updateAiAgent: builder.mutation<AiAgent, { id: string; data: Partial<AiAgent> }>({
      query: ({ id, data }) => ({ url: `/v1/ai-agents/${id}`, method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents"]),
    }),
    deleteAiAgent: builder.mutation<AiAgent, string>({
      query: (id) => ({ url: `/v1/ai-agents/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents"]),
    }),
    keepAiAgent: builder.mutation<AiAgent, string>({
      query: (id) => ({ url: `/v1/ai-agents/${id}/keep`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents"]),
    }),
    toggleAiAgent: builder.mutation<AiAgent, string>({
      query: (id) => ({ url: `/v1/ai-agents/${id}/toggle`, method: "patch" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents"]),
    }),
    getAiAgentCalls: builder.query<
      PagedResponse<AiAgentCall>,
      { agentId: string; direction?: "INBOUND" | "OUTBOUND"; page?: number; size?: number }
    >({
      query: ({ agentId, direction, page = 0, size = 20 }) => ({
        url: `/v1/ai-agents/${agentId}/calls`,
        method: "get",
        params: {
          page,
          size,
          sort: "createdAt,desc",
          ...(direction ? { direction } : {}),
        },
      }),
      providesTags: ["AiAgentCalls"],
    }),
    getAiAgentCall: builder.query<AiAgentCall, { agentId: string; callId: string }>({
      query: ({ agentId, callId }) => ({
        url: `/v1/ai-agents/${agentId}/calls/${callId}`,
        method: "get",
      }),
      providesTags: ["AiAgentCalls"],
    }),
    /**
     * AI calls in progress right now. Served from the backend's in-memory session registry, since
     * an AiAgentCall row only exists once the call has ended. Poll this — there's no push for the
     * list itself; the transcript/sentiment of an individual call stream over websockets.
     */
    getLiveAiCalls: builder.query<LiveAiCall[], void>({
      query: () => ({ url: "/v1/ai-agents/live-calls", method: "get" }),
      providesTags: ["AiAgentLiveCalls"],
    }),
    /**
     * Backfill for one live call. Fetched only when a call is opened — an agent can run several
     * calls at once, so shipping every transcript with the polled list would scale badly.
     */
    getLiveAiCallTranscript: builder.query<TranscriptLine[], string>({
      query: (callId) => ({ url: `/v1/ai-agents/live-calls/${callId}/transcript`, method: "get" }),
    }),
    getAgentPhoneNumbers: builder.query<PhoneNumber[], string>({
      query: (agentId) => ({ url: `/v1/ai-agents/${agentId}/phone-numbers`, method: "get" }),
      providesTags: ["AiAgents", "PhoneNumbers"],
    }),
    assignPhoneNumberToAgent: builder.mutation<PhoneNumber, { agentId: string; phoneNumberId: string }>({
      query: ({ agentId, phoneNumberId }) => ({
        url: `/v1/ai-agents/${agentId}/phone-numbers/${phoneNumberId}`,
        method: "put",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents", "PhoneNumbers"]),
    }),
    unassignPhoneNumberFromAgent: builder.mutation<PhoneNumber, { agentId: string; phoneNumberId: string }>({
      query: ({ agentId, phoneNumberId }) => ({
        url: `/v1/ai-agents/${agentId}/phone-numbers/${phoneNumberId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents", "PhoneNumbers"]),
    }),
    placeAgentTestCall: builder.mutation<
      AiAgent,
      { agentId: string; toPhoneNumber: string; fromPhoneNumberId?: string }
    >({
      query: ({ agentId, toPhoneNumber, fromPhoneNumberId }) => ({
        url: `/v1/ai-agents/${agentId}/test-call`,
        method: "post",
        data: { toPhoneNumber, ...(fromPhoneNumberId ? { fromPhoneNumberId } : {}) },
      }),
    }),
    takeoverAiCall: builder.mutation<
      Record<string, string>,
      { agentId: string; callId: string; phoneNumber?: string }
    >({
      query: ({ agentId, callId, phoneNumber }) => ({
        url: `/v1/ai-agents/${agentId}/calls/${callId}/takeover`,
        method: "post",
        data: phoneNumber ? { phoneNumber } : {},
      }),
      // The call leaves the live list once the AI stops, so refresh that too.
      invalidatesTags: (_, error) => (error ? [] : ["AiAgentCalls", "AiAgentLiveCalls"]),
    }),

    // AI Marketplace
    getAiMarketplaceListings: builder.query<PagedResponse<AiAgentListing>, { category?: string; page?: number; size?: number }>({
      query: ({ category, page = 0, size = 20 }) => ({
        url: "/v1/ai-marketplace/listings",
        method: "get",
        params: { page, size, ...(category ? { category } : {}) },
      }),
      providesTags: ["AiMarketplace"],
    }),
    getAiMarketplaceListing: builder.query<AiAgentListing, string>({
      query: (id) => ({ url: `/v1/ai-marketplace/listings/${id}`, method: "get" }),
      providesTags: ["AiMarketplace"],
    }),
    acquireAiAgent: builder.mutation<AiAgent, string>({
      query: (listingId) => ({ url: `/v1/ai-marketplace/listings/${listingId}/acquire`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiAgents", "AiMarketplace"]),
    }),
    getFeatureCatalog: builder.query<FeatureCatalogEntry[], void>({
      query: () => ({ url: `/v1/features`, method: "get" }),
      providesTags: ["Features"],
    }),
    getEnabledFeatures: builder.query<OrgFeature[], void>({
      query: () => ({ url: `/v1/features/enabled`, method: "get" }),
      providesTags: ["Features"],
    }),
    toggleFeature: builder.mutation<OrgFeature, OrgFeatureToggleRequest>({
      query: (body) => ({ url: `/v1/features/toggle`, method: "post", data: body }),
      invalidatesTags: (_, error) => (error ? [] : ["Features", "Home"]),
    }),
    updateActiveOrganisation: builder.mutation<Organisation, Partial<Organisation>>({
      query: (body) => ({ url: `/v1/organisations/active`, method: "patch", data: body }),
      invalidatesTags: (_, error) => (error ? [] : ["Organisations", "Home"]),
    }),
    getOnboardingState: builder.query<OnboardingState, void>({
      query: () => ({ url: `/v1/onboarding/state`, method: "get" }),
      providesTags: ["Onboarding"],
    }),
    submitOnboarding: builder.mutation<unknown, OnboardingSubmission>({
      query: (body) => ({ url: `/v1/onboarding/submit`, method: "post", data: body }),
      invalidatesTags: (_, error) =>
        error ? [] : ["Onboarding", "Features", "Home", "Checklist"],
    }),
    skipOnboardingStep: builder.mutation<OnboardingState, string>({
      query: (stepKey) => ({ url: `/v1/onboarding/skip-step/${stepKey}`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Onboarding"]),
    }),
    getChecklist: builder.query<Checklist, void>({
      query: () => ({ url: `/v1/dashboard/checklist`, method: "get" }),
      providesTags: ["Checklist"],
    }),
    skipChecklistItem: builder.mutation<Checklist, string>({
      query: (itemKey) => ({ url: `/v1/dashboard/checklist/${itemKey}/skip`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["Checklist"]),
    }),
  }),
});

export const {
  useGetSiteTemplatesQuery,
  useGetSitesQuery,
  useGetSiteQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useDeleteSiteMutation,
  usePublishSiteMutation,
  useUnpublishSiteMutation,
  useGenerateSitePreviewTokenMutation,
  useGetSitePagesQuery,
  useUpdateSitePageMutation,
  useGetSiteFormsQuery,
  useUpdateSiteFormMutation,
  useGetSiteLeadsQuery,
  useGetSiteRecentLeadCountQuery,
  useMarkSiteLeadAsSpamMutation,
  useGetDashboardSummaryQuery,
  useGetHomeQuery,
  useLazyGetHomeQuery,
  usePutHomeMutation,
  useSetActiveOrganisationMutation,
  useGetUserPresenceQuery,
  useUpdateUserPresenceMutation,
  useUpdateWorkingHoursMutation,
  useSetMemberWorkingHoursMutation,
  usePostLoginMutation,
  useTwoFactorEnrollMutation,
  useTwoFactorSendMutation,
  useTwoFactorVerifyMutation,
  useGetTwoFactorStatusQuery,
  useTwoFactorSmsSetupMutation,
  useTwoFactorSmsConfirmMutation,
  useTwoFactorTotpSetupMutation,
  useTwoFactorTotpConfirmMutation,
  useTwoFactorSendCodeMutation,
  useDisableTwoFactorMutation,
  useGetDevicesQuery,
  useRevokeDeviceMutation,
  useStartOAuthLoginMutation,
  useCompleteOAuthLoginMutation,
  useGetAuthIdentitiesQuery,
  useUnlinkAuthIdentityMutation,
  usePostRegisterMutation,
  useSubmitRegistrationRequestMutation,
  useGetReferralProgramStatusQuery,
  useValidateInviteQuery,
  useGetMembershipInvitationQuery,
  usePostRegisterViaInviteMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  usePostFileMutation,
  usePostAuthZSalesforceMutation,
  usePostAuthSalesforceMutation,
  useGetIntegrationsQuery,
  useCreateIntegrationMutation,
  useSyncIntegrationMutation,
  useDeleteIntegrationMutation,
  useCreateCallMutation,
  useGetOrganisationsQuery,
  useCreateOrganisationMutation,
  useGetOwnPhoneNumbersQuery,
  useUpdatePhoneNumberMutation,
  useAssignCallFlowToPhoneNumberMutation,
  usePurchasePhoneNumberMutation,
  useSubmitTollfreeVerificationMutation,
  useRefreshTollfreeVerificationMutation,
  useReleasePhoneNumberMutation,
  useLazyGetAvailablePhoneNumbersQuery,
  useCreatePaymentIntentMutation,
  useAttachPaymentMethodMutation,
  useCancelSubscriptionMutation,
  useKeepSubscriptionMutation,
  useGetSubscriptionQuery,
  useGetSubscriptionPreviewQuery,
  useCreateIdentityVerificationSessionMutation,
  useGetPlansQuery,
  useGetSupportChatTokenQuery,
  useGetAddOnsQuery,
  useGetAddOnPricesQuery,
  useGetAiListingChargesQuery,
  useSetAddOnMutation,
  useCreateSubscriptionMutation,
  useDetachPaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useGetBillingOverviewQuery,
  useGetReferralsQuery,
  useGetBillingUsageQuery,
  useGetPaymentMethodsQuery,
  useUpdateSubscriptionMutation,
  useGetDepositsQuery,
  useGetChargesQuery,
  useGetChargeQuery,
  useGetBillingDetailsQuery,
  useUpdateBillingDetailsMutation,
  useGetInvoicesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoicePdfLinkQuery,
  useSetupAutoTopUpMutation,
  useGetCallFlowsQuery,
  useGetCallFlowQuery,
  useCreateCallFlowMutation,
  useUpdateCallFlowMutation,
  useDeleteCallFlowMutation,
  useDuplicateCallFlowMutation,
  useGetCallFlowRevisionsQuery,
  useRestoreCallFlowRevisionMutation,
  useToggleCallFlowMutation,
  useGetMembersQuery,
  useGetOwnMembershipQuery,
  useUpdateOwnVoicemailGreetingMutation,
  useUpdateOwnContactWarningPreferenceMutation,
  useInviteMemberMutation,
  useAcceptInvitationMutation,
  useUpdateMemberRoleMutation,
  useAssignDialerSeatMutation,
  useSetExportAccessMutation,
  useSetMemberDisplayNameMutation,
  useUpdateOwnDisplayNameMutation,
  useRemoveMemberMutation,
  useLeaveOrganisationMutation,
  useResendInvitationMutation,
  useInitiateCallMutation,
  useEndCallMutation,
  useArchiveCallMutation,
  useRestoreCallMutation,
  useDeleteCallPermanentlyMutation,
  useHoldCallMutation,
  useReportCallPlatformMutation,
  useGetCallParticipantsQuery,
  useAddCallParticipantMutation,
  useHoldCallParticipantMutation,
  useResumeCallParticipantMutation,
  useEndCallParticipantMutation,
  useTransferCallMutation,
  useGetMemberAvailabilityQuery,
  useGetCallQuery,
  useLazyGetIncomingCallQuery,
  useGetCallsQuery,
  useGetCallStatsQuery,
  useLazyGetCallsQuery,
  useLazyGetCallRecordingUrlQuery,
  useMarkCallRecordingReviewedMutation,
  useSetCallRecordingStateMutation,
  useGetTelephonySettingsQuery,
  useLazyGetTelephonySettingsQuery,
  useUpdateCallerIdMutation,
  useUpdateLineTypeIntelligenceMutation,
  useUpdateMachineDetectionMutation,
  useUpdateTranscriptionMutation,
  useUpdateCallSummariesMutation,
  useUpdateBusyTeammateRingingMutation,
  useCreateCallSummaryTaskMutation,
  useGetCallBySidQuery,
  useLazyGetCallBySidQuery,
  useLazyGetActiveCallQuery,
  useGetCampaignContactsQuery,
  useGetCampaignContactIdsQuery,
  useGetSequenceStatsQuery,
  useGetCampaignEnrollmentsQuery,
  useRemoveCampaignContactsMutation,
  useLazyGetCampaignContactsQuery,
  useStartCampaignMutation,
  useGetCampaignCompliancePreflightQuery,
  useCheckCampaignDncNumbersMutation,
  useLazyCheckQuietHoursQuery,
  useGetSmsEligibilityQuery,
  useGetQuietHoursPolicyQuery,
  useUpdateQuietHoursPolicyMutation,
  useGetOptOutKeywordsQuery,
  useAddOptOutKeywordMutation,
  useRemoveOptOutKeywordMutation,
  useGetA2pPricingQuery,
  useStopCampaignMutation,
  useHoldCampaignCallMutation,
  useResumeCampaignCallMutation,
  useDropCampaignVoicemailMutation,
  useEndCampaignCallMutation,
  useSetCampaignDispositionMutation,
  useSaveCampaignNotesMutation,
  useStartDialerSessionMutation,
  useEndDialerSessionMutation,
  useGetDialerSessionQuery,
  useLazyGetDialerSessionSnapshotQuery,
  useMarkDialerSessionReadyMutation,
  useStartDialerSessionBreakMutation,
  useTakeDialerReturnCallMutation,
  useHangUpDialerReturnCallMutation,
  useGetDialerQueueQuery,
  useGetDialerQueueCountsQuery,
  useGetPreviewLeadQuery,
  useGetVoicemailDropStatusQuery,
  useGetDialerNumbersQuery,
  useSetDialerNumberDailyCapMutation,
  useSetDialerReturnCallDaysMutation,
  useRestDialerNumberMutation,
  useRestoreDialerNumberMutation,
  useTryCampaignLeadNumberMutation,
  useScheduleLeadCallbackMutation,
  useRequeueCampaignOutcomesMutation,
  useMarkLeadDoNotCallMutation,
  useMarkLeadWrongNumberMutation,
  usePreviewDialNextMutation,
  useSkipCampaignContactMutation,
  useGetLiveMonitorCallsQuery,
  useGetDialerReportQuery,
  useGetDialerSessionHistoryQuery,
  useGetAbandonmentReportQuery,
  useStartMonitorMutation,
  useGetCallSupervisorSessionsQuery,
  useGetCallFlowStepsQuery,
  usePreviewVoiceMutation,
  useGetAudioClipsQuery,
  useGenerateAudioClipMutation,
  useUploadAudioClipMutation,
  useRenameAudioClipMutation,
  useDeleteAudioClipMutation,
  useGetCampaignQuery,
  useLazyGetCampaignQuery,
  useGetCampaignsQuery,
  useGetCampaignAudiencePreviewQuery,
  useGetContactImportsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useArchiveCampaignMutation,
  useUnarchiveCampaignMutation,
  useGetContactsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useArchiveContactMutation,
  useRestoreContactMutation,
  useDeleteContactPermanentlyMutation,
  useGetContactDuplicatesQuery,
  useMergeContactsMutation,
  useAddCampaignContactsMutation,
  useAddCampaignContactsByPhoneMutation,
  useStartAiCampaignMutation,
  useGetAiCampaignCompliancePreflightQuery,
  usePauseAiCampaignMutation,
  useStopAiCampaignMutation,
  useGetAiCampaignLiveQuery,
  useGetContactCallsQuery,
  useGetContactMessagesQuery,
  useGetContactActivitySummaryQuery,
  useGetConversationsQuery,
  useLazyGetConversationsQuery,
  useGetConversationQuery,
  useGetMessagesQuery,
  useSendMessageMutation,
  useGetTextsReportQuery,
  useMarkConversationReadMutation,
  useAddParticipantMutation,
  useArchiveConversationMutation,
  useRestoreConversationMutation,
  useDeleteConversationPermanentlyMutation,
  useFlagConversationMutation,
  useGetUnreadTotalQuery,
  useGetCommunicationItemsInfiniteQuery,
  useGetAttentionCountQuery,
  useMarkCommunicationItemsSeenMutation,
  useArchiveThreadMutation,
  useRestoreThreadMutation,
  useRebuildCommunicationItemsMutation,
  useGetContactListsQuery,
  useGetContactListQuery,
  useCreateContactListMutation,
  useUpdateContactListMutation,
  useDeleteContactListMutation,
  useGetContactListContactsQuery,
  useAddContactsToListMutation,
  useRemoveContactFromListMutation,
  useSkipTraceContactMutation,
  useSkipTraceContactListMutation,
  useSkipTraceSelectionMutation,
  useGetSkipTracePreviewQuery,
  useGetSkipTraceSelectionPreviewQuery,
  useGetSkipTraceJobQuery,
  useGetSkipTraceJobsQuery,
  useGetSkipTraceJobResultsQuery,
  useGetConsentHistoryQuery,
  useGrantConsentMutation,
  useGrantConsentByNumberMutation,
  useRevokeConsentMutation,
  useGetDncEntriesQuery,
  useAddDncEntryMutation,
  useRemoveDncEntryMutation,
  useRemoveFromDncListMutation,
  useGetEmailSuppressionsQuery,
  useAddEmailSuppressionMutation,
  useRemoveEmailSuppressionMutation,
  useGetUnsubscribeTargetQuery,
  useUnsubscribeEmailMutation,
  useCheckDncMutation,
  useGetDncLookupPricesQuery,
  useLookUpDncNumbersMutation,
  useGetCallDispositionsQuery,
  useCreateCallDispositionMutation,
  useUpdateCallDispositionMutation,
  useDeleteCallDispositionMutation,
  useGetCallScriptsQuery,
  useCreateCallScriptMutation,
  useUpdateCallScriptMutation,
  useDeleteCallScriptMutation,
  useGetTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useBulkAddTagMutation,
  useBulkRemoveTagMutation,
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useUpdateTaskStatusMutation,
  useDeleteTaskMutation,
  useGetWorkflowsQuery,
  useGetWorkflowQuery,
  useCreateWorkflowMutation,
  useUpdateWorkflowMutation,
  useUpdateWorkflowStatusMutation,
  useDeleteWorkflowMutation,
  useGetWebhookCredentialsQuery,
  useCreateWebhookCredentialMutation,
  useUpdateWebhookCredentialMutation,
  useDeleteWebhookCredentialMutation,
  useGetWorkflowExecutionsQuery,
  useCancelWorkflowExecutionMutation,
  useUpdateCallMutation,
  useCreateMessageNoteMutation,
  useUpdateMessageNoteMutation,
  useDeleteMessageNoteMutation,
  useConfirmPossibleOptOutMutation,
  useDismissPossibleOptOutMutation,
  useGetDealsQuery,
  useGetDealQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useUpdateDealStageMutation,
  useDeleteDealMutation,
  useGetDealActivitiesQuery,
  useGetPipelineSummaryQuery,
  useGetDealStagesQuery,
  useCreateDealStageMutation,
  useUpdateDealStageConfigMutation,
  useReorderDealStagesMutation,
  useMakeDefaultDealStageMutation,
  useGetDealStageUsageQuery,
  useDeleteDealStageMutation,
  useGetDealStageSettingsQuery,
  useUpdateLeadAutoPromoteMutation,
  useGetLeadSummaryQuery,
  usePromoteContactMutation,
  useConvertContactMutation,
  useUndoConversionMutation,
  useGetPropertiesQuery,
  useGetPropertyQuery,
  useGetPropertyContactsQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useLinkPropertyContactMutation,
  useUnlinkPropertyContactMutation,
  useUploadFilesMutation,
  useCreateImportMutation,
  useSelectImportSheetMutation,
  useGetImportValuesMutation,
  usePreviewImportMutation,
  useRunImportMutation,
  useGetImportRunsQuery,
  useGetImportRunQuery,
  useLazyGetImportRunErrorsQuery,
  useUndoImportRunMutation,
  useCreateExportMutation,
  useGetExportsQuery,
  useGetExportQuery,
  useLazyGetExportDownloadQuery,
  useSearchExternalPropertiesMutation,
  useImportExternalPropertiesMutation,
  useGetPropertyDataUsageQuery,
  usePreviewListingsMutation,
  useCheckDuplicateSearchMutation,
  useStartListingsBuildMutation,
  useGetListingsBuildStatusQuery,
  useImportNextListingsMutation,
  useGetListingsSearchesQuery,
  useGetA2pRegistrationQuery,
  useSaveA2pDraftMutation,
  useSubmitA2pCustomerProfileMutation,
  useSubmitA2pBrandMutation,
  useGenerateA2pContentMutation,
  useCreateA2pMessagingServiceMutation,
  useSubmitA2pCampaignMutation,
  useRefreshA2pStatusMutation,
  useCancelA2pRegistrationMutation,
  useResendA2pOtpMutation,
  usePurchaseAssistedA2pMutation,
  useGetA2pCampaignsQuery,
  useCreateA2pCampaignMutation,
  useUpdateA2pCampaignMutation,
  useSubmitA2pCampaignByIdMutation,
  useCancelA2pCampaignByIdMutation,
  useAssignA2pCampaignNumbersMutation,
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkNotificationsSeenMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useGetAiAgentsQuery,
  useGetAiAgentQuery,
  useGetAiVoicesQuery,
  useGetAgentVoicesQuery,
  useGetAiOutcomesQuery,
  useUpdateAiAgentMutation,
  useDeleteAiAgentMutation,
  useToggleAiAgentMutation,
  useKeepAiAgentMutation,
  useGetAiAgentCallsQuery,
  useGetAiAgentCallQuery,
  useGetLiveAiCallsQuery,
  useGetLiveAiCallTranscriptQuery,
  useLazyGetLiveAiCallTranscriptQuery,
  useTakeoverAiCallMutation,
  useGetAgentPhoneNumbersQuery,
  useAssignPhoneNumberToAgentMutation,
  useUnassignPhoneNumberFromAgentMutation,
  usePlaceAgentTestCallMutation,
  useGetAiMarketplaceListingsQuery,
  useGetAiMarketplaceListingQuery,
  useAcquireAiAgentMutation,
  useUpdateIntegrationSettingsMutation,
  useGetSalesforceRealtimeStatusQuery,
  useCheckSalesforceRealtimeMutation,
  useUpdateIntegrationSyncSettingsMutation,
  useUpdateFieldMappingMutation,
  useGetDefaultFieldMappingsQuery,
  useGetSalesforceUsersQuery,
  useSetSalesforceUserMappingMutation,
  useGetSyncLogsQuery,
  useGetActivityLogQuery,
  useGetPushJobsQuery,
  useGetPushJobSummaryQuery,
  useRetryPushJobMutation,
  useGetSyncLogSummaryQuery,
  useRetrySyncLogMutation,
  usePostAuthZHubSpotMutation,
  usePostAuthHubSpotMutation,
  usePostAuthZGhlMutation,
  usePostAuthGhlMutation,
  useConnectInvestorFuseMutation,
  useConnectReiSiftMutation,
  useGetHubSpotOwnersQuery,
  useSetHubSpotUserMappingMutation,
  usePostAuthZPodioMutation,
  usePostAuthPodioMutation,
  useGetPodioOrganizationsQuery,
  useGetPodioWorkspacesQuery,
  useGetPodioAppsQuery,
  useGetPodioAppFieldsQuery,
  useCreatePodioCallsAppMutation,
  useCreatePodioContactsAppMutation,
  useSetupPodioWebhookMutation,
  useSearchPodioIconsQuery,
  useLinkPodioAppMutation,
  useSetPodioUserMappingMutation,
  useDescribeSalesforceObjectQuery,
  useGetSalesforceListViewsQuery,
  useImportSalesforceListViewMutation,
  useGetFeatureCatalogQuery,
  useGetEnabledFeaturesQuery,
  useToggleFeatureMutation,
  useUpdateActiveOrganisationMutation,
  useGetOnboardingStateQuery,
  useSubmitOnboardingMutation,
  useSkipOnboardingStepMutation,
  useGetChecklistQuery,
  useSkipChecklistItemMutation,
} = restApi;

export type RestApiType = typeof restApi;
